<template>
    <v-container class="mt-10" max-width="1200">
        <v-skeleton-loader type="table" v-if="loading" />

        <div v-else-if="meeting">
            <!-- Meeting Header Card -->
            <v-card elevation="2" class="mb-6 meeting-header-card">
                <v-card-text>
                    <div class="d-flex align-center justify-space-between mb-4">
                        <div>
                            <h1 class="text-h4 font-weight-bold mb-2">{{ meeting?.name || "Untitled Meeting" }}</h1>
                            <div class="meeting-meta d-flex align-center flex-wrap">
                                <v-chip small :color="getMeetingStatusColor()" class="mr-3 mb-2">
                                    {{ getMeetingStatusName() }}
                                </v-chip>
                                <div class="meeting-date mr-3 mb-2">
                                    <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
                                    <span class="text-caption grey--text">
                                        {{ formatMeetingDate(meeting.meeting_date || meeting.created_at) }}
                                    </span>
                                </div>
                                <div v-if="meeting.start_time || meeting.end_time" class="meeting-time mr-3 mb-2">
                                    <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                                    <span class="text-caption grey--text">
                                        {{ formatMeetingTimeRange(meeting.meeting_date, meeting.start_time, meeting.end_time) }}
                                    </span>
                                </div>
                                <div v-if="meeting.frequency" class="meeting-frequency mr-3 mb-2">
                                    <v-icon small color="grey" class="mr-1">mdi-repeat</v-icon>
                                    <v-chip x-small :color="getMeetingFrequencyColor(meeting.frequency)"
                                        text-color="white">
                                        {{ meeting.frequency === 'once' ? 'One-time' : 'Recurring' }}
                                    </v-chip>
                                </div>
                            </div>

                            <!-- Additional Meeting Details Row -->
                            <div class="meeting-details mt-3">
                                <v-row>
                                    <v-col cols="12" md="6" v-if="meeting.company">
                                        <div class="d-flex align-center">
                                            <v-icon small color="primary" class="mr-2">mdi-office-building</v-icon>
                                            <div>
                                                <span class="text-caption grey--text">Company:</span>
                                                <span class="ml-1 font-weight-medium">{{ meeting.company.name }}</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="6" v-if="getCreatorName(meeting)">
                                        <div class="d-flex align-center">
                                            <v-icon small color="primary" class="mr-2">mdi-account-circle</v-icon>
                                            <div>
                                                <span class="text-caption grey--text">Creator:</span>
                                                <span class="ml-1 font-weight-medium">{{ getCreatorName(meeting)
                                                    }}</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="6" v-if="getNoteTakerName(meeting)">
                                        <div class="d-flex align-center">
                                            <v-icon small color="secondary" class="mr-2">mdi-notebook-edit</v-icon>
                                            <div>
                                                <span class="text-caption grey--text">Note Taker:</span>
                                                <span class="ml-1 font-weight-medium">{{ getNoteTakerName(meeting)
                                                    }}</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="6" v-if="meeting.memo_url">
                                        <div class="d-flex align-center">
                                            <v-icon small color="info" class="mr-2">mdi-link</v-icon>
                                            <div>
                                                <span class="text-caption grey--text">Memo:</span>
                                                <a :href="ensureExternalUrl(meeting.memo_url)" target="_blank"
                                                    class="ml-1 text-decoration-none">
                                                    <v-btn x-small color="info" text class="pa-0">
                                                        <v-icon left x-small>mdi-open-in-new</v-icon>
                                                        View Document
                                                    </v-btn>
                                                </a>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="6" v-if="meeting.conference_link">
                                        <div class="d-flex align-center">
                                            <v-icon small color="success" class="mr-2">mdi-video</v-icon>
                                            <div>
                                                <span class="text-caption grey--text">Conference:</span>
                                                <a :href="ensureExternalUrl(meeting.conference_link)" target="_blank"
                                                    @click="handleJoinMeeting"
                                                    class="ml-1 text-decoration-none">
                                                    <v-btn x-small color="success" text class="pa-0">
                                                        <v-icon left x-small>mdi-open-in-new</v-icon>
                                                        Join Meeting
                                                    </v-btn>
                                                </a>
                                            </div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Recurrence Pattern (shown only for recurring meetings) -->
                            <div v-if="meeting.frequency === 'recurring'" class="recurrence-pattern mt-4">
                                <v-card outlined class="pa-3">
                                    <div class="d-flex align-center mb-2">
                                        <v-icon small color="primary" class="mr-2">mdi-repeat</v-icon>
                                        <span class="text-subtitle-2 font-weight-medium">Recurrence Pattern</span>
                                    </div>
                                    <div class="recurrence-details">
                                        <v-row>
                                            <v-col cols="12" md="6">
                                                <div class="d-flex align-center">
                                                    <v-icon x-small color="grey" class="mr-2">mdi-calendar-clock</v-icon>
                                                    <span class="text-body-2">
                                                        Every {{ meeting.recurrence_interval || 1 }} 
                                                        {{ (meeting.recurrence_interval || 1) === 1 ? 'week' : 'weeks' }}
                                                    </span>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="meeting.recurrence_days && meeting.recurrence_days.length > 0">
                                                <div class="d-flex align-center">
                                                    <v-icon x-small color="grey" class="mr-2">mdi-calendar-week</v-icon>
                                                    <span class="text-body-2">On {{ formatRecurrenceDays(meeting.recurrence_days) }}</span>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="meeting.series_start_date">
                                                <div class="d-flex align-center">
                                                    <v-icon x-small color="grey" class="mr-2">mdi-calendar-start</v-icon>
                                                    <span class="text-body-2">
                                                        Series starts: {{ formatMeetingDate(meeting.series_start_date) }}
                                                    </span>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="meeting.series_end_date">
                                                <div class="d-flex align-center">
                                                    <v-icon x-small color="grey" class="mr-2">mdi-calendar-end</v-icon>
                                                    <span class="text-body-2">
                                                        Series ends: {{ formatMeetingDate(meeting.series_end_date) }}
                                                    </span>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-card>
                            </div>

                            <!-- Announcement Banner -->
                            <v-alert v-if="meeting.announcement" type="info" text dense class="mt-3"
                                icon="mdi-bullhorn">
                                <div class="text-caption">
                                    <strong>Announcement:</strong> {{ meeting.announcement }}
                                </div>
                            </v-alert>
                        </div>
                        <div class="d-flex flex-column">
                            <v-btn @click="toggleEditMode()" v-if="canEditMeeting" color="primary" large class="text-none mb-2">
                                <v-icon left>mdi-pencil</v-icon>
                                Edit Meeting
                            </v-btn>
                            <v-btn 
                                v-if="meeting.conference_link" 
                                @click="startRecordingBot(meeting.id)" 
                                color="secondary" 
                                outlined 
                                large 
                                class="text-none"
                            >
                                <v-icon left>mdi-robot</v-icon>
                                Start Recording Bot
                            </v-btn>
                        </div>
                    </div>

                    <!-- Meeting Attendees -->
                    <div class="meeting-attendees mb-4">
                        <h3 class="text-h6 mb-3">Meeting Attendees</h3>

                        <div class="attendees-section">
                            <div v-if="meeting.access && meeting.access.length > 0" class="current-attendees">
                                <div class="d-flex flex-wrap align-center">
                                    <v-chip v-for="access in meeting.access" :key="access.user_id"
                                        :color="getAvatarColor(access.user?.name || 'Unknown')" text-color="white"
                                        class="mr-2 mb-2 attendee-chip">
                                        <v-avatar left size="24">
                                            <span class="text-caption font-weight-bold">
                                                {{ getInitials(access.user?.name || 'U') }}
                                            </span>
                                        </v-avatar>
                                        {{ access.user?.name || "Unknown User" }}
                                    </v-chip>
                                </div>
                            </div>
                            <div v-else class="no-attendees">
                                <p class="grey--text">No attendees assigned to this meeting.</p>
                            </div>
                        </div>
                    </div>

                    <!-- Meeting Agenda -->
                    <div class="meeting-agenda mb-4">
                        <h3 class="text-h6 mb-3">Meeting Agenda</h3>
                        <div class="agenda-display">
                            <div v-if="meeting.agenda" v-html="meeting.agenda" class="agenda-content"
                                style="overflow-y:auto;max-height:600px;"></div>
                            <p v-else class="grey--text">No agenda has been set for this meeting.</p>
                        </div>
                    </div>

                    <!-- Meeting Attachments -->
                    <div v-if="meetingAttachments && meetingAttachments.length > 0" class="meeting-attachments">
                        <h3 class="text-h6 mb-3">
                            <v-icon class="mr-2" color="primary">mdi-paperclip</v-icon>
                            Meeting Attachments
                        </h3>
                        <v-row>
                            <v-col v-for="attachment in meetingAttachments" :key="attachment.id" cols="12" sm="6"
                                md="4">
                                <v-card outlined hover class="attachment-card">
                                    <v-card-text class="pa-3">
                                        <div class="d-flex align-center mb-2">
                                            <v-icon :color="getFileTypeColor(attachment.file_type)" size="32"
                                                class="mr-3">
                                                {{ getFileTypeIcon(attachment.file_type) }}
                                            </v-icon>
                                            <div class="flex-grow-1">
                                                <h4 class="text-subtitle-2 font-weight-medium mb-1">
                                                    {{ attachment.name }}
                                                </h4>
                                                <div class="text-caption grey--text">
                                                    {{ attachment.size }} • {{ attachment.file_type?.toUpperCase() }}
                                                </div>
                                            </div>
                                        </div>
                                        <p v-if="attachment.description" class="text-body-2 grey--text mb-2">
                                            {{ attachment.description }}
                                        </p>
                                        <div class="d-flex justify-end">
                                            <v-btn small color="primary" outlined
                                                @click="downloadAttachment(attachment)" class="mr-2">
                                                <v-icon left small>mdi-download</v-icon>
                                                Download
                                            </v-btn>
                                            <v-btn v-if="attachment.url" small color="primary" text
                                                :href="getAttachmentViewUrl(attachment.url)" target="_blank">
                                                <v-icon left small>mdi-open-in-new</v-icon>
                                                View
                                            </v-btn>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </div>
                </v-card-text>
            </v-card>

            <!-- Meeting Tasks & Resources Section -->
            <v-card elevation="2" class="meeting-content-card">
                <v-tabs v-model="activeTab" background-color="transparent" color="primary">
                    <v-tab>
                        <v-icon left>mdi-clipboard-check-outline</v-icon>
                        Meeting Tasks
                        <v-chip x-small class="ml-2" v-if="filteredTasks.length > 0">
                            {{ filteredTasks.length }}
                        </v-chip>
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-folder-multiple</v-icon>
                        Resources
                        <v-chip x-small class="ml-2" v-if="meetingResources.length > 0">
                            {{ meetingResources.length }}
                        </v-chip>
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-video</v-icon>
                        Summary
                    </v-tab>
                </v-tabs>

                <v-divider></v-divider>

                <v-tabs-items v-model="activeTab">
                    <!-- Meeting Tasks Tab -->
                    <v-tab-item>
                        <v-card-title class="meeting-tasks-header">
                            <v-spacer></v-spacer>

                            <!-- Task Filters -->
                            <div class="task-filters d-flex align-center">
                                <v-select v-if="availableUsers.length > 0" v-model="selectedUserId" :items="userFilterOptions"
                                    item-text="name" item-value="id" label="Filter by User" outlined dense clearable
                                    class="user-filter mr-3" style="max-width: 200px;">
                                    <template v-slot:selection="{ item }">
                                        <v-chip small :color="getAvatarColor(item.name)">
                                            {{ item.name }}
                                        </v-chip>
                                    </template>
                                </v-select>

                                <TaskCreationDialog v-model="create_task_dialog" :users="availableUsers"
                                    :companies="meeting.company ? [meeting.company] : []" :meeting-id="meeting_id"
                                    task-type="assign" :is-approval-mode="true" @tasks-created="onTasksCreated" @close="closeCreateTaskDialog" />

                                <v-btn @click="openCreateTaskDialog()" color="primary" class="text-none">
                                    <v-icon left>mdi-plus</v-icon>
                                    Add Task
                                </v-btn>
                            </div>
                        </v-card-title>

                        <v-card-text>
                            <div class="d-flex align-center mb-3" style="gap: 12px;">
                                <v-autocomplete v-model="meetingStatusFilters" :items="statusFilterOptions"
                                    item-text="name" item-value="id" label="Status" dense outlined multiple
                                    chips hide-details style="max-width: 320px;">
                                    <template v-slot:prepend-inner>
                                        <v-icon color="primary" size="16">mdi-format-list-checks</v-icon>
                                    </template>
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip v-if="index < 2" small close
                                            @click:close="meetingStatusFilters.splice(meetingStatusFilters.indexOf(item.id), 1)">
                                            {{ item.name }}
                                        </v-chip>
                                        <span v-if="index === 2" class="text-grey text-caption">
                                            (+{{ meetingStatusFilters.length - 2 }} others)
                                        </span>
                                    </template>
                                </v-autocomplete>
                            </div>
                            <div v-if="filteredTasks.length > 0 || get_user.role_id === 1">
                                <UnifiedTreeViewDashboard taskType="assigned" :tasks="filteredTasks" :users="availableUsers"
                                    :companies="meeting.company ? [meeting.company] : []" :meeting-id="meeting_id"
                                    @updateTaskData="updateTaskData" @optimisticUpdate="handleOptimisticUpdate"
                                    :containerHeight="500" />
                            </div>
                            <div v-else class="empty-tasks text-center py-8">
                                <v-icon size="64" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
                                <h3 class="text-h5 grey--text mt-4 mb-2">No tasks yet</h3>
                                <p class="text-body-1 grey--text mb-4">
                                    Create tasks to track action items from this meeting.
                                </p>
                                <v-btn @click="openCreateTaskDialog()" color="primary" large>
                                    <v-icon left>mdi-plus</v-icon>
                                    Create Task
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-tab-item>

                    <!-- Resources Tab -->
                    <v-tab-item>
                        <v-card-text>
                            <!-- Action Button -->
                            <div class="d-flex justify-end mb-4">
                                <v-btn color="primary" @click="showUploadResourceDialog = true" class="text-none">
                                    <v-icon left>mdi-upload</v-icon>
                                    Upload Resource
                                </v-btn>
                            </div>

                            <!-- Meeting Resources List -->
                            <div v-if="meetingResources && meetingResources.length > 0" class="meeting-resources">
                                <v-row>
                                    <v-col v-for="resource in meetingResources" :key="resource.id" cols="12" sm="6" md="4">
                                        <v-card outlined hover class="resource-card">
                                            <v-card-text class="pa-3">
                                                <div class="d-flex align-center mb-2">
                                                    <v-icon :color="getFileTypeColor(resource.file_type)" size="32" class="mr-3">
                                                        {{ getFileTypeIcon(resource.file_type) }}
                                                    </v-icon>
                                                    <div class="flex-grow-1">
                                                        <h4 class="text-subtitle-2 font-weight-medium mb-1">
                                                            {{ resource.name }}
                                                        </h4>
                                                        <v-chip v-if="resource.category" x-small class="mt-1">
                                                            {{ resource.category }}
                                                        </v-chip>
                                                        <div v-if="resource.file_type" class="text-caption grey--text mt-1">
                                                            {{ resource.file_type?.toUpperCase() }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <p v-if="resource.description" class="text-body-2 grey--text mb-3" style="max-height: 60px; overflow: hidden;">
                                                    {{ resource.description }}
                                                </p>
                                                <div class="d-flex justify-space-between align-center">
                                                    <div>
                                                        <v-btn small color="primary" outlined @click="downloadResource(resource)" class="mr-2">
                                                            <v-icon left small>mdi-download</v-icon>
                                                            Download
                                                        </v-btn>
                                                        <v-btn v-if="resource.type === 'url' && resource.url" small color="primary" text
                                                            :href="resource.url" target="_blank">
                                                            <v-icon left small>mdi-open-in-new</v-icon>
                                                            Open
                                                        </v-btn>
                                                    </div>
                                                    <v-btn v-if="canEditMeeting" icon small color="error" @click="detachResource(resource.id)">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-else class="empty-resources text-center py-8">
                                <v-icon size="64" color="grey lighten-1">mdi-folder-open-outline</v-icon>
                                <h3 class="text-h5 grey--text mt-4 mb-2">No resources yet</h3>
                                <p class="text-body-1 grey--text mb-4">
                                    Upload documents, files, and other resources for this meeting.
                                </p>
                                <v-btn color="primary" @click="showUploadResourceDialog = true" class="text-none">
                                    <v-icon left>mdi-upload</v-icon>
                                    Upload Resource
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-tab-item>

                    <!-- Video Tab -->
                    <v-tab-item>
                        <v-card-text class="pa-0">
                            <!-- Recording toolbar (selector + add transcript) -->
                            <div v-if="!loadingRecording && allRecordings.length >= 1" class="recording-selector pa-4" style="background-color: #fafafa; border-bottom: 1px solid #e0e0e0;">
                                <v-row align="center">
                                    <v-col cols="12" :md="allRecordings.length > 1 ? 6 : 'auto'">
                                        <div class="d-flex align-center">
                                            <v-select
                                                v-if="allRecordings.length > 1"
                                                v-model="selectedRecordingId"
                                                :items="recordingSelectOptions"
                                                item-text="label"
                                                item-value="id"
                                                label="Select Recording Occurrence"
                                                outlined
                                                dense
                                                hide-details
                                                prepend-inner-icon="mdi-calendar-clock"
                                                @change="loadSelectedRecording"
                                            >
                                                <template v-slot:selection="{ item }">
                                                    <v-chip small color="primary">
                                                        <v-icon left x-small>mdi-calendar</v-icon>
                                                        {{ item.label }}
                                                    </v-chip>
                                                </template>
                                                <template v-slot:item="{ item }">
                                                    <v-list-item-content>
                                                        <v-list-item-title>
                                                            <v-icon small class="mr-2">mdi-calendar</v-icon>
                                                            {{ item.label }}
                                                        </v-list-item-title>
                                                    </v-list-item-content>
                                                </template>
                                            </v-select>
                                            <v-btn
                                                small
                                                color="teal"
                                                dark
                                                :class="allRecordings.length > 1 ? 'ml-3' : ''"
                                                class="flex-shrink-0"
                                                @click="showManualTranscriptDialog = true; manualTranscriptText = ''"
                                            >
                                                <v-icon left small>mdi-text-box-plus-outline</v-icon>
                                                Add Transcript
                                            </v-btn>
                                            <v-tooltip bottom v-if="meeting && (Number(meeting.user_id) === Number(get_user.id) || [1, 2, 11].includes(get_user.role_id)) && selectedRecordingId">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn
                                                        small
                                                        icon
                                                        color="red darken-1"
                                                        class="ml-2 flex-shrink-0"
                                                        :loading="deletingRecording"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        @click="deleteRecording"
                                                    >
                                                        <v-icon small>mdi-delete-outline</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Delete this recording</span>
                                            </v-tooltip>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" :md="allRecordings.length > 1 ? 6 : ''" class="d-flex align-center justify-end">
                                        <v-chip small outlined>
                                            <v-icon left small>mdi-video-vintage</v-icon>
                                            {{ allRecordings.length }} Recording{{ allRecordings.length !== 1 ? 's' : '' }} Available
                                        </v-chip>
                                    </v-col>
                                </v-row>
                            </div>

                            <div v-if="loadingRecording" class="text-center py-8">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                <p class="mt-4 grey--text">Loading recording...</p>
                            </div>

                            <!-- Recording is being processed by Recall AI -->
                            <div v-else-if="isRecordingProcessing">
                                <!-- If we already have the video, show robot animation + video/transcript below -->
                                <template v-if="recordingData && recordingData.download_url">
                                    <!-- Robot animation banner -->
                                    <div class="ai-summary-processing-state" style="padding-bottom: 24px; border-bottom: 1px solid #e0e0e0;">
                                        <div class="d-flex align-center justify-center mb-6">
                                            <span style="font-size: 1.5rem; font-weight: 700; color: #1a1a2e; letter-spacing: -0.5px;">AI Meeting Summary</span>
                                            <span class="ml-2" style="font-size: 1.4rem;">✨</span>
                                        </div>
                                        <div class="ai-robot-illustration mb-6">
                                            <div class="robot-bubble robot-bubble-left"><v-icon color="blue lighten-2" size="20">mdi-text</v-icon></div>
                                            <div class="robot-bubble robot-bubble-top"><v-icon color="blue lighten-2" size="16">mdi-text</v-icon></div>
                                            <div class="robot-bubble robot-bubble-right"><v-icon color="amber" size="20">mdi-lightbulb-outline</v-icon></div>
                                            <div class="robot-avatar">
                                                <v-avatar size="104" color="blue darken-1">
                                                    <v-icon dark size="60">mdi-robot-happy-outline</v-icon>
                                                </v-avatar>
                                                <div class="robot-laptop"><v-icon color="blue darken-2" size="32">mdi-laptop</v-icon></div>
                                            </div>
                                        </div>
                                        <h3 class="text-h6 font-weight-bold mb-5" style="color: #1a1a2e;">Generating meeting summary...</h3>
                                        <v-progress-circular indeterminate color="blue lighten-2" size="80" width="6" class="mb-6"></v-progress-circular>
                                        <p class="text-body-2 mb-1" style="color: #999; max-width: 260px; margin: 0 auto;">
                                            Please wait a moment while we process your summary... ✨
                                        </p>
                                        <div class="d-flex align-center justify-center mt-4">
                                            <v-chip small color="blue lighten-4" class="mr-2" style="color: #1565c0;">
                                                <v-icon left x-small>mdi-refresh</v-icon>
                                                Auto-refreshing every 10 seconds
                                            </v-chip>
                                        </div>
                                    </div>

                                    <!-- Full video + transcript layout (reused from below) -->
                                    <div class="video-transcript-layout">
                                        <v-row no-gutters style="height: calc(100vh - 380px); min-height: 550px;">
                                            <!-- Left: Video -->
                                            <v-col cols="12" md="7" class="video-section" style="height: 100%; display: flex; flex-direction: column;">
                                                <div class="pa-4" style="flex-shrink: 0;">
                                                    <h3 class="text-h6 mb-2">Meeting Recording</h3>
                                                    <div class="d-flex align-center mb-3">
                                                        <v-chip small :color="getCurrentRecordingStatusColor()" class="mr-2">
                                                            {{ getCurrentRecordingStatus() }}
                                                        </v-chip>
                                                        <span v-if="recordingData.occurrence_date || recordingData.created_at" class="text-caption grey--text">
                                                            <template v-if="recordingData.occurrence_date">
                                                                Occurrence: {{ formatRecordingDate(recordingData.occurrence_date) }}
                                                            </template>
                                                            <template v-else>
                                                                Started: {{ formatRecordingDate(recordingData.created_at) }}
                                                            </template>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div class="px-4" style="flex-shrink: 0;">
                                                    <video
                                                        ref="videoPlayer"
                                                        :key="selectedRecordingId"
                                                        controls
                                                        class="meeting-video"
                                                        style="width: 100%; border-radius: 8px; background: #000;"
                                                        @timeupdate="handleVideoTimeUpdate"
                                                    >
                                                        <source :src="recordingData.download_url" type="video/mp4">
                                                        Your browser does not support the video tag.
                                                    </video>
                                                </div>
                                            </v-col>
                                            <!-- Right: Transcript -->
                                            <v-col cols="12" md="5" class="transcript-section" style="height: 100%; border-left: 1px solid #e0e0e0;">
                                                <div class="pa-4 d-flex align-center" style="border-bottom: 1px solid #e0e0e0;">
                                                    <v-icon class="mr-2">mdi-text-box-outline</v-icon>
                                                    <h3 class="text-subtitle-1 font-weight-bold mb-0">Transcript</h3>
                                                    <v-chip x-small color="success" class="ml-2" v-if="currentVideoTime > 0 && transcriptData && transcriptData.words">
                                                        <v-icon x-small left>mdi-sync</v-icon>
                                                        Live
                                                    </v-chip>
                                                </div>
                                                <div
                                                    ref="transcriptContainer"
                                                    class="transcript-scroll-area pa-4"
                                                    style="height: calc(100% - 64px); overflow-y: auto; overflow-x: hidden;"
                                                >
                                                    <div v-if="loadingTranscript" class="text-center py-8">
                                                        <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                                                        <p class="mt-4 text-caption grey--text">Loading transcript...</p>
                                                    </div>
                                                    <div v-else-if="transcriptData && transcriptData.words && transcriptData.words.length > 0">
                                                        <div
                                                            v-for="(segment, index) in formatTranscriptSegments(transcriptData.words)"
                                                            :key="index"
                                                            :ref="'segment-' + index"
                                                            class="transcript-segment mb-4 pa-3 rounded"
                                                            :class="{ 'active-segment': isSegmentActive(segment) }"
                                                            :style="isSegmentActive(segment) ? 'background-color: #e3f2fd; border-left: 3px solid #1976d2; transition: all 0.2s;' : 'border-left: 3px solid transparent;'"
                                                            @click="seekToSegment(segment.start_timestamp)"
                                                            style="cursor: pointer;"
                                                        >
                                                            <div class="d-flex align-start">
                                                                <v-chip
                                                                    x-small
                                                                    class="mr-2 flex-shrink-0"
                                                                    :color="isSegmentActive(segment) ? 'primary' : 'grey lighten-1'"
                                                                    :dark="isSegmentActive(segment)"
                                                                    style="min-width: 45px;"
                                                                >
                                                                    {{ formatTimestamp(segment.start_timestamp) }}
                                                                </v-chip>
                                                                <div class="flex-grow-1">
                                                                    <div v-if="segment.speaker" class="font-weight-medium mb-1" style="font-size: 0.875rem;">
                                                                        {{ segment.speaker }}
                                                                    </div>
                                                                    <div class="text-body-2" style="line-height: 1.5;">
                                                                        {{ segment.text }}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-else class="text-center py-8">
                                                        <v-icon size="48" color="grey lighten-1">mdi-text-box-remove-outline</v-icon>
                                                        <p class="mt-4 text-body-2 grey--text">No transcript available yet</p>
                                                    </div>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </div>
                                </template>

                                <!-- No video yet — show full robot animation -->
                                <template v-else>
                                    <div class="ai-summary-processing-state">
                                        <!-- Header -->
                                        <div class="d-flex align-center justify-center mb-6">
                                            <span style="font-size: 1.5rem; font-weight: 700; color: #1a1a2e; letter-spacing: -0.5px;">
                                                AI Meeting Summary
                                            </span>
                                            <span class="ml-2" style="font-size: 1.4rem;">✨</span>
                                        </div>
                                        <!-- Robot illustration -->
                                        <div class="ai-robot-illustration mb-6">
                                            <div class="robot-bubble robot-bubble-left">
                                                <v-icon color="blue lighten-2" size="20">mdi-text</v-icon>
                                            </div>
                                            <div class="robot-bubble robot-bubble-top">
                                                <v-icon color="blue lighten-2" size="16">mdi-text</v-icon>
                                            </div>
                                            <div class="robot-bubble robot-bubble-right">
                                                <v-icon color="amber" size="20">mdi-lightbulb-outline</v-icon>
                                            </div>
                                            <div class="robot-avatar">
                                                <v-avatar size="104" color="blue darken-1">
                                                    <v-icon dark size="60">mdi-robot-happy-outline</v-icon>
                                                </v-avatar>
                                                <div class="robot-laptop">
                                                    <v-icon color="blue darken-2" size="32">mdi-laptop</v-icon>
                                                </div>
                                            </div>
                                        </div>
                                        <h3 class="text-h6 font-weight-bold mb-5" style="color: #1a1a2e;">
                                            Generating meeting summary...
                                        </h3>
                                        <v-progress-circular indeterminate color="blue lighten-2" size="80" width="6" class="mb-6"></v-progress-circular>
                                        <p class="text-body-2 mb-1" style="color: #999; max-width: 260px; margin: 0 auto;">
                                            Please wait a moment while we process your summary... ✨
                                        </p>
                                        <div class="d-flex align-center justify-center mt-4">
                                            <v-chip small color="blue lighten-4" class="mr-2" style="color: #1565c0;">
                                                <v-icon left x-small>mdi-refresh</v-icon>
                                                Auto-refreshing every 10 seconds
                                            </v-chip>
                                        </div>
                                    </div>
                                </template>
                            </div>

                            <!-- Manual transcript recording: has summary but no video -->
                            <div v-else-if="regeneratingSummary" class="ai-summary-processing-state">
                                <!-- Header -->
                                <div class="d-flex align-center justify-center mb-6">
                                    <span style="font-size: 1.5rem; font-weight: 700; color: #1a1a2e; letter-spacing: -0.5px;">
                                        AI Meeting Summary
                                    </span>
                                    <span class="ml-2" style="font-size: 1.4rem;">✨</span>
                                </div>

                                <!-- Robot illustration -->
                                <div class="ai-robot-illustration mb-6">
                                    <div class="robot-bubble robot-bubble-left">
                                        <v-icon color="blue lighten-2" size="20">mdi-text</v-icon>
                                    </div>
                                    <div class="robot-bubble robot-bubble-top">
                                        <v-icon color="blue lighten-2" size="16">mdi-text</v-icon>
                                    </div>
                                    <div class="robot-bubble robot-bubble-right">
                                        <v-icon color="amber" size="20">mdi-lightbulb-outline</v-icon>
                                    </div>
                                    <div class="robot-avatar">
                                        <v-avatar size="104" color="blue darken-1">
                                            <v-icon dark size="60">mdi-robot-happy-outline</v-icon>
                                        </v-avatar>
                                        <div class="robot-laptop">
                                            <v-icon color="blue darken-2" size="32">mdi-laptop</v-icon>
                                        </div>
                                    </div>
                                </div>

                                <!-- Status text -->
                                <h3 class="text-h6 font-weight-bold mb-5" style="color: #1a1a2e;">
                                    Regenerating meeting summary...
                                </h3>

                                <!-- Dotted circular spinner -->
                                <v-progress-circular
                                    indeterminate
                                    color="blue lighten-2"
                                    size="80"
                                    width="6"
                                    class="mb-6"
                                ></v-progress-circular>

                                <!-- Sub-text -->
                                <p class="text-body-2 mb-1" style="color: #999; max-width: 260px; margin: 0 auto;">
                                    Please wait a moment while we process your summary... ✨
                                </p>
                            </div>

                            <!-- Manual transcript recording: has summary but no video -->
                            <div v-else-if="recordingData && !recordingData.download_url && transcriptData && transcriptData.summary" class="pa-4">
                                <div class="meeting-summary-section" style="background-color: #f5f5f5; border-radius: 8px;">
                                    <v-expansion-panels flat v-model="summaryPanelOpen">
                                        <v-expansion-panel>
                                            <v-expansion-panel-header class="pa-4">
                                                <div class="d-flex align-center justify-space-between" style="width: 100%;">
                                                    <div class="d-flex align-center">
                                                        <v-icon class="mr-2" color="teal">mdi-text-box-check-outline</v-icon>
                                                        <h3 class="text-h6 font-weight-bold mb-0">AI Meeting Summary</h3>
                                                        <v-chip x-small color="teal" dark class="ml-2">
                                                            <v-icon x-small left>mdi-robot</v-icon>
                                                            From Transcript
                                                        </v-chip>
                                                    </div>
                                                    <div class="d-flex align-center">
                                                        <v-btn
                                                            v-if="recordingData && recordingData.type === 'manual_transcript'"
                                                            small
                                                            color="teal"
                                                            outlined
                                                            @click.stop="openEditTranscriptDialog"
                                                            class="mr-2"
                                                        >
                                                            <v-icon left small>mdi-pencil-outline</v-icon>
                                                            Edit Transcript
                                                        </v-btn>
                                                        <v-btn
                                                            v-if="meeting.user_id === get_user.id || get_user.role_id === 1"
                                                            small
                                                            color="primary"
                                                            outlined
                                                            :loading="regeneratingSummary"
                                                            @click.stop="regenerateSummaryFromManual"
                                                        >
                                                            <v-icon left small>mdi-refresh</v-icon>
                                                            Regenerate
                                                        </v-btn>
                                                        <template v-if="meeting.user_id === get_user.id">
                                                            <v-btn
                                                                v-if="!editingSummary"
                                                                small
                                                                color="orange darken-1"
                                                                outlined
                                                                @click.stop="startEditSummary"
                                                                class="ml-2"
                                                            >
                                                                <v-icon left small>mdi-pencil</v-icon>
                                                                Edit Summary
                                                            </v-btn>
                                                            <template v-else>
                                                                <v-btn
                                                                    small
                                                                    color="success"
                                                                    :loading="savingSummary"
                                                                    @click.stop="saveEditedSummary"
                                                                    class="ml-2"
                                                                >
                                                                    <v-icon left small>mdi-content-save</v-icon>
                                                                    Save
                                                                </v-btn>
                                                                <v-btn
                                                                    small
                                                                    text
                                                                    @click.stop="cancelEditSummary"
                                                                    class="ml-1"
                                                                >
                                                                    Cancel
                                                                </v-btn>
                                                            </template>
                                                        </template>
                                                        <v-btn
                                                            v-if="meeting.user_id === get_user.id || get_user.role_id === 1"
                                                            small
                                                            color="teal"
                                                            outlined
                                                            :loading="sendingSummaryEmail"
                                                            @click.stop="sendSummaryEmail"
                                                            class="ml-2"
                                                        >
                                                            Send Summary Email
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content>
                                                <v-card outlined class="mt-2">
                                                    <v-card-text class="summary-content pa-4" style="max-height: 600px; overflow-y: auto;">
                                                        <div v-if="!editingSummary" class="markdown-content" v-html="transcriptData.summary"></div>
                                                        <div v-else>
                                                            <wysiwyg v-model="editedSummaryContent" />
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </div>
                            </div>

                            <div v-else-if="recordingData && recordingData.download_url" class="video-transcript-layout">

                                <!-- AI-Generated Meeting Summary -->
                                <div v-if="transcriptData && transcriptData.summary" class="meeting-summary-section pa-4" style="background-color: #f5f5f5; border-bottom: 1px solid #e0e0e0;">
                                    <v-expansion-panels flat v-model="summaryPanelOpen">
                                        <v-expansion-panel>
                                            <v-expansion-panel-header class="pa-4">
                                                <div class="d-flex align-center justify-space-between" style="width: 100%;">
                                                    <div class="d-flex align-center">
                                                        <v-icon class="mr-2" color="primary">mdi-file-document-outline</v-icon>
                                                        <h3 class="text-h6 font-weight-bold mb-0">AI Meeting Summary</h3>
                                                        <v-chip x-small color="success" class="ml-2">
                                                            <v-icon x-small left>mdi-robot</v-icon>
                                                            AI Generated
                                                        </v-chip>
                                                    </div>
                                                    <div class="d-flex align-center">
                                                        <v-btn 
                                                            v-if="meeting.user_id === get_user.id || get_user.role_id === 1"
                                                            small 
                                                            color="primary" 
                                                            outlined
                                                            :loading="regeneratingSummary"
                                                            @click.stop="regenerateSummary"
                                                        >
                                                            <v-icon left small>mdi-refresh</v-icon>
                                                            Regenerate
                                                        </v-btn>
                                                        
                                                        <template v-if="meeting.user_id === get_user.id">
                                                            <v-btn
                                                                v-if="!editingSummary"
                                                                small
                                                                color="orange darken-1"
                                                                outlined
                                                                @click.stop="startEditSummary"
                                                                class="ml-2"
                                                            >
                                                                <v-icon left small>mdi-pencil</v-icon>
                                                                Edit Summary
                                                            </v-btn>
                                                            <template v-else>
                                                                <v-btn
                                                                    small
                                                                    color="success"
                                                                    :loading="savingSummary"
                                                                    @click.stop="saveEditedSummary"
                                                                    class="ml-2"
                                                                >
                                                                    <v-icon left small>mdi-content-save</v-icon>
                                                                    Save
                                                                </v-btn>
                                                                <v-btn
                                                                    small
                                                                    text
                                                                    @click.stop="cancelEditSummary"
                                                                    class="ml-1"
                                                                >
                                                                    Cancel
                                                                </v-btn>
                                                            </template>
                                                        </template>
                                                        <v-btn 
                                                            v-if="meeting.user_id === get_user.id || get_user.role_id === 1"
                                                            small 
                                                            color="teal" 
                                                            outlined
                                                            :loading="sendingSummaryEmail"
                                                            @click.stop="sendSummaryEmail"
                                                            class="ml-2"
                                                        >
                                                            Send Summary Email
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content>
                                                <v-card outlined class="mt-2">
                                                    <v-card-text class="summary-content pa-4" style="max-height: 600px; overflow-y: auto;">
                                                        <!-- Render the full summary HTML with all headers and structure -->
                                                        <div v-if="!editingSummary" class="markdown-content" v-html="transcriptData.summary"></div>
                                                        <div v-else>
                                                            <wysiwyg v-model="editedSummaryContent" />
                                                        </div>

                                                        <!-- Hidden checkbox tracking for Vue reactivity -->
                                                        <div style="display: none;">
                                                            <div v-for="(section, index) in parsedSummary" :key="'section-' + index">
                                                                <input 
                                                                    v-if="section.type === 'checkbox'"
                                                                    type="checkbox" 
                                                                    v-model="section.userChecked"
                                                                    :disabled="section.alreadyCompleted"
                                                                />
                                                            </div>
                                                        </div>

                                                        <!-- Bulk Add to Tasks Button -->
                                                        <div v-if="checkedChecklistItems.length > 0" class="mt-4 pt-4" style="border-top: 1px solid #e0e0e0;">
                                                            <v-btn
                                                                color="primary"
                                                                @click="showBulkTaskDialog = true"
                                                                class="text-none"
                                                            >
                                                                <v-icon left>mdi-plus-circle-multiple</v-icon>
                                                                Add {{ checkedChecklistItems.length }} Item{{ checkedChecklistItems.length > 1 ? 's' : '' }} to Tasks
                                                            </v-btn>
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </div>

                                <!-- Two Column Layout: Video Left, Transcript Right -->
                                <v-row no-gutters style="height: calc(100vh - 350px); min-height: 600px;">
                                    <!-- Left: Video Section -->
                                    <v-col cols="12" md="7" class="video-section" style="height: 100%; display: flex; flex-direction: column;">
                                        <div class="pa-4" style="flex-shrink: 0;">
                                            <h3 class="text-h6 mb-2">Meeting Recording</h3>
                                            <div class="d-flex align-center mb-3">
                                                <v-chip small :color="getCurrentRecordingStatusColor()" class="mr-2">
                                                    {{ getCurrentRecordingStatus() }}
                                                </v-chip>
                                                <span v-if="recordingData.occurrence_date || recordingData.created_at" class="text-caption grey--text">
                                                    <template v-if="recordingData.occurrence_date">
                                                        Occurrence: {{ formatRecordingDate(recordingData.occurrence_date) }}
                                                    </template>
                                                    <template v-else>
                                                        Started: {{ formatRecordingDate(recordingData.created_at) }}
                                                    </template>
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Video Player -->
                                        <div class="px-4" style="flex-shrink: 0;">
                                            <video 
                                                ref="videoPlayer"
                                                :key="selectedRecordingId"
                                                v-if="recordingData.download_url" 
                                                controls 
                                                class="meeting-video" 
                                                style="width: 100%; border-radius: 8px; background: #000;"
                                                @timeupdate="handleVideoTimeUpdate"
                                            >
                                                <source :src="recordingData.download_url" type="video/mp4">
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>

                                        <!-- Recording Actions -->
                                        <div class="pa-4" style="flex-shrink: 0;">
                                            <div class="d-flex flex-wrap gap-2">
                                                <v-btn small text color="primary" @click="fetchRecordingUrl">
                                                    <v-icon left small>mdi-refresh</v-icon>
                                                    Refresh
                                                </v-btn>
                                            </div>
                                        </div>
                                    </v-col>

                                    <!-- Right: Transcript Section -->
                                    <v-col cols="12" md="5" class="transcript-section" style="height: 100%; border-left: 1px solid #e0e0e0;">
                                        <div class="pa-4 d-flex align-center" style="border-bottom: 1px solid #e0e0e0;">
                                            <v-icon class="mr-2">mdi-text-box-outline</v-icon>
                                            <h3 class="text-subtitle-1 font-weight-bold mb-0">Transcript</h3>
                                            <v-chip x-small color="success" class="ml-2" v-if="currentVideoTime > 0 && transcriptData && transcriptData.words">
                                                <v-icon x-small left>mdi-sync</v-icon>
                                                Live
                                            </v-chip>
                                        </div>
                                        
                                        <div 
                                            ref="transcriptContainer" 
                                            class="transcript-scroll-area pa-4" 
                                            style="height: calc(100% - 64px); overflow-y: auto; overflow-x: hidden;"
                                        >
                                            <div v-if="loadingTranscript" class="text-center py-8">
                                                <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                                                <p class="mt-4 text-caption grey--text">Loading transcript...</p>
                                            </div>

                                            <div v-else-if="transcriptData && transcriptData.words && transcriptData.words.length > 0">
                                                <div 
                                                    v-for="(segment, index) in formatTranscriptSegments(transcriptData.words)" 
                                                    :key="index" 
                                                    :ref="'segment-' + index"
                                                    class="transcript-segment mb-4 pa-3 rounded"
                                                    :class="{ 'active-segment': isSegmentActive(segment) }"
                                                    :style="isSegmentActive(segment) ? 'background-color: #e3f2fd; border-left: 3px solid #1976d2; transition: all 0.2s;' : 'border-left: 3px solid transparent;'"
                                                    @click="seekToSegment(segment.start_timestamp)"
                                                    style="cursor: pointer;"
                                                >
                                                    <div class="d-flex align-start">
                                                        <v-chip 
                                                            x-small 
                                                            class="mr-2 flex-shrink-0" 
                                                            :color="isSegmentActive(segment) ? 'primary' : 'grey lighten-1'"
                                                            :dark="isSegmentActive(segment)"
                                                            style="min-width: 45px;"
                                                        >
                                                            {{ formatTimestamp(segment.start_timestamp) }}
                                                        </v-chip>
                                                        <div class="flex-grow-1">
                                                            <div v-if="segment.speaker" class="font-weight-medium mb-1" style="font-size: 0.875rem;">
                                                                {{ segment.speaker }}
                                                            </div>
                                                            <div class="text-body-2" style="line-height: 1.5;">
                                                                {{ segment.text }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-else class="text-center py-8">
                                                <v-icon size="48" color="grey lighten-1">mdi-text-box-remove-outline</v-icon>
                                                <p class="mt-4 text-body-2 grey--text">No transcript available</p>
                                            </div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Recording exists but no video URL and no summary yet -->
                            <div v-else-if="recordingData && !recordingData.download_url" class="pa-4">
                                <div class="text-center py-6">
                                    <v-icon size="48" color="orange">mdi-video-check-outline</v-icon>
                                    <h3 class="text-h6 grey--text text--darken-1 mt-3 mb-2">Recording Available</h3>
                                    <p class="text-body-2 grey--text mb-1">
                                        This recording does not have a video file, but you can add a transcript to generate an AI summary.
                                    </p>
                                    <div class="d-flex align-center justify-center mt-2 mb-4">
                                        <v-chip small outlined class="mr-2">
                                            <v-icon left small>mdi-identifier</v-icon>
                                            ID: {{ recordingData.db_id || recordingData.id }}
                                        </v-chip>
                                        <v-chip small outlined v-if="recordingData.type">
                                            <v-icon left small>mdi-tag-outline</v-icon>
                                            {{ recordingData.type }}
                                        </v-chip>
                                        <v-chip small outlined v-if="recordingData.occurrence_date || recordingData.created_at" class="ml-2">
                                            <v-icon left small>mdi-calendar</v-icon>
                                            {{ formatRecordingDate(recordingData.occurrence_date || recordingData.created_at) }}
                                        </v-chip>
                                    </div>
                                    <div class="d-flex justify-center flex-wrap gap-3">
                                        <v-btn color="primary" outlined @click="fetchRecordingUrl" class="mr-3">
                                            <v-icon left>mdi-refresh</v-icon>
                                            Refresh Recording
                                        </v-btn>
                                        <v-btn color="teal" dark @click="showManualTranscriptDialog = true; manualTranscriptText = ''">
                                            <v-icon left>mdi-text-box-plus-outline</v-icon>
                                            Add Transcript
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Manual Transcript Summary Result (shown inline after generation) -->
                                <div v-if="manualTranscriptSummary" class="mt-4 text-left">
                                    <v-card outlined>
                                        <v-card-title class="teal lighten-5">
                                            <v-icon left color="teal">mdi-file-document-outline</v-icon>
                                            <span class="teal--text text--darken-2">AI Meeting Summary</span>
                                            <v-chip x-small color="teal" dark class="ml-2">
                                                <v-icon x-small left>mdi-robot</v-icon>
                                                From Transcript
                                            </v-chip>
                                            <v-spacer></v-spacer>
                                            <v-btn
                                                v-if="meeting.user_id === get_user.id || get_user.role_id === 1"
                                                small
                                                color="teal"
                                                outlined
                                                :loading="sendingSummaryEmail"
                                                @click="sendSummaryEmail"
                                            >
                                                Send Summary Email
                                            </v-btn>
                                        </v-card-title>
                                        <v-card-text class="pa-4">
                                            <div class="markdown-content" v-html="manualTranscriptSummary"></div>
                                        </v-card-text>
                                    </v-card>
                                </div>
                            </div>

                            <div v-else-if="recordingError" class="empty-recording text-center py-8">
                                <v-icon size="64" color="error">mdi-alert-circle-outline</v-icon>
                                <h3 class="text-h5 grey--text mt-4 mb-2">{{ recordingError }}</h3>
                                <div class="d-flex justify-center flex-wrap mt-4">
                                    <v-btn color="primary" outlined @click="fetchRecordingUrl" class="mr-3">
                                        <v-icon left>mdi-refresh</v-icon>
                                        Try Again
                                    </v-btn>
                                    <v-btn color="teal" dark @click="showManualTranscriptDialog = true">
                                        <v-icon left>mdi-text-box-plus-outline</v-icon>
                                        Add Meeting Transcript
                                    </v-btn>
                                </div>

                                <!-- Manual Transcript Summary Result -->
                                <div v-if="manualTranscriptSummary" class="mt-6 text-left">
                                    <v-card outlined>
                                        <v-card-title class="teal lighten-5">
                                            <v-icon left color="teal">mdi-file-document-outline</v-icon>
                                            <span class="teal--text text--darken-2">AI Meeting Summary</span>
                                            <v-chip x-small color="teal" dark class="ml-2">
                                                <v-icon x-small left>mdi-robot</v-icon>
                                                From Transcript
                                            </v-chip>
                                            <v-spacer></v-spacer>
                                            <v-btn
                                                v-if="meeting.user_id === get_user.id || get_user.role_id === 1"
                                                small
                                                color="teal"
                                                outlined
                                                :loading="sendingSummaryEmail"
                                                @click="sendSummaryEmail"
                                            >
                                                Send Summary Email
                                            </v-btn>
                                        </v-card-title>
                                        <v-card-text class="pa-4">
                                            <div class="markdown-content" v-html="manualTranscriptSummary"></div>
                                        </v-card-text>
                                    </v-card>
                                </div>
                            </div>

                            <div v-else class="empty-recording text-center py-8">
                                <v-icon size="64" color="grey lighten-1">mdi-video-off-outline</v-icon>
                                <h3 class="text-h5 grey--text mt-4 mb-2">No recording available</h3>
                                <p class="text-body-1 grey--text mb-4">
                                    The recording for this meeting is not yet available or does not exist.
                                </p>
                                <div class="d-flex justify-center flex-wrap gap-3">
                                    <v-btn color="primary" outlined @click="fetchRecordingUrl" class="mr-3">
                                        <v-icon left>mdi-refresh</v-icon>
                                        Check for Recording
                                    </v-btn>
                                    <v-btn color="teal" dark @click="showManualTranscriptDialog = true">
                                        <v-icon left>mdi-text-box-plus-outline</v-icon>
                                        Add Meeting Transcript
                                    </v-btn>
                                </div>

                                <!-- Manual Transcript Summary Result (shown inline after generation) -->
                                <div v-if="manualTranscriptSummary" class="mt-6 text-left">
                                    <v-card outlined>
                                        <v-card-title class="teal lighten-5">
                                            <v-icon left color="teal">mdi-file-document-outline</v-icon>
                                            <span class="teal--text text--darken-2">AI Meeting Summary</span>
                                            <v-chip x-small color="teal" dark class="ml-2">
                                                <v-icon x-small left>mdi-robot</v-icon>
                                                From Transcript
                                            </v-chip>
                                            <v-spacer></v-spacer>
                                            <v-btn
                                                v-if="meeting.user_id === get_user.id || get_user.role_id === 1"
                                                small
                                                color="teal"
                                                outlined
                                                :loading="sendingSummaryEmail"
                                                @click="sendSummaryEmail"
                                            >
                                                Send Summary Email
                                            </v-btn>
                                        </v-card-title>
                                        <v-card-text class="pa-4">
                                            <div class="markdown-content" v-html="manualTranscriptSummary"></div>
                                        </v-card-text>
                                    </v-card>
                                </div>
                            </div>
                        </v-card-text>
                    </v-tab-item>
                </v-tabs-items>
            </v-card>
        </div>

        <!-- Error/Empty state for meeting not found -->
        <div v-else class="text-center pa-8">
            <v-icon size="64" color="grey">mdi-calendar-remove</v-icon>
            <h3 class="text-h5 grey--text mt-4 mb-2">Meeting not found</h3>
            <p class="text-body-1 grey--text mb-4">The meeting you're looking for doesn't exist or you don't have access
                to it.
            </p>
            <v-btn color="primary" @click="$router.push('/meetings')">
                <v-icon left>mdi-arrow-left</v-icon>
                Back to Meetings
            </v-btn>
        </div>

        <!-- Edit Meeting Dialog -->
        <EditMeetingDialog v-model="edit_meeting_dialog" :meeting-data="meeting" :users="users()"
            :companies="companies()" @update-meeting="handleUpdateMeeting" />

        <!-- Upload Meeting Resource Dialog - Only render when meeting_id is available -->
        <UploadMeetingResourceDialog
            v-if="meeting_id"
            v-model="showUploadResourceDialog"
            :meeting-id="meeting_id"
            :meeting-company-id="meeting?.company_id"
            @resource-uploaded="handleResourceUploaded"
        />

        <!-- Delete Recording Confirmation Dialog -->
        <v-dialog v-model="showDeleteRecordingDialog" max-width="460" persistent>
            <v-card>
                <v-card-title class="red darken-1 white--text">
                    <v-icon left color="white">mdi-delete-alert-outline</v-icon>
                    Delete Recording
                </v-card-title>
                <v-card-text class="pa-6">
                    <div class="d-flex align-start">
                        <v-icon color="red darken-1" size="40" class="mr-4 mt-1">mdi-alert-circle-outline</v-icon>
                        <div>
                            <p class="text-body-1 font-weight-medium mb-1">Are you sure you want to delete this recording?</p>
                            <p class="text-body-2 grey--text mb-0">This will permanently remove the recording, transcript, and AI summary. This action cannot be undone.</p>
                        </div>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        @click="showDeleteRecordingDialog = false"
                        :disabled="deletingRecording"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="red darken-1"
                        dark
                        :loading="deletingRecording"
                        @click="confirmDeleteRecording"
                    >
                        <v-icon left small>mdi-delete-outline</v-icon>
                        Delete Recording
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Manual Transcript Dialog -->
        <v-dialog v-model="showManualTranscriptDialog" max-width="700" scrollable>
            <v-card>
                <v-card-title class="teal darken-1 white--text">
                    <v-icon left color="white">mdi-text-box-plus-outline</v-icon>
                    Add Meeting Transcript
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="showManualTranscriptDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-4">
                    <p class="text-body-2 grey--text mb-3">
                        Paste or type the meeting transcript below. An AI summary will be generated from it.
                    </p>
                    <v-textarea
                        v-model="manualTranscriptText"
                        outlined
                        rows="12"
                        label="Meeting Transcript"
                        placeholder="Paste your meeting transcript here..."
                        :disabled="generatingManualSummary"
                        counter
                        no-resize
                    ></v-textarea>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="showManualTranscriptDialog = false" :disabled="generatingManualSummary">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="teal"
                        dark
                        :loading="generatingManualSummary"
                        :disabled="!manualTranscriptText.trim()"
                        @click="submitManualTranscript"
                    >
                        <v-icon left>mdi-content-save-outline</v-icon>
                        Save & Generate Summary
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Bulk Task Assignment Dialog -->
        <v-dialog v-model="showBulkTaskDialog" max-width="800" scrollable>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-playlist-check</v-icon>
                    Add Checklist Items to Tasks
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="showBulkTaskDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-4" style="max-height: 500px;">
                    <p class="text-body-2 grey--text mb-4">
                        Assign team members to each task. You can select the same person for multiple tasks.
                    </p>

                    <v-list>
                        <v-list-item v-for="(item, index) in checkedChecklistItems" :key="'checked-' + index" class="mb-3 pa-3" style="border: 1px solid #e0e0e0; border-radius: 8px;">
                            <v-list-item-content>
                                <div class="d-flex align-start justify-space-between mb-2">
                                    <v-list-item-title style="white-space: normal; flex-grow: 1;">
                                        <v-icon small class="mr-2" color="primary">mdi-checkbox-marked</v-icon>
                                        {{ item.text }}
                                    </v-list-item-title>
                                    <v-btn icon x-small color="error" @click="item.userChecked = false" class="ml-2">
                                        <v-icon x-small>mdi-close</v-icon>
                                    </v-btn>
                                </div>
                                
                                <!-- Show suggestion if available -->
                                <div v-if="item.suggestedFor" class="mb-2">
                                    <v-chip x-small color="info" outlined>
                                        <v-icon x-small left>mdi-lightbulb-on-outline</v-icon>
                                        Task Suggested for: {{ item.suggestedFor }}
                                    </v-chip>
                                </div>
                                
                                <div v-if="item.selectedAssignee" class="mb-2">
                                    <v-chip x-small color="success" outlined>
                                        <v-icon x-small left>mdi-account-check</v-icon>
                                        {{ getAssigneeName(item.selectedAssignee) }}
                                    </v-chip>
                                </div>
                                <v-autocomplete
                                    v-model="item.selectedAssignee"
                                    :items="availableUsers"
                                    item-text="name"
                                    item-value="id"
                                    label="Assign to"
                                    outlined
                                    dense
                                    clearable
                                    prepend-inner-icon="mdi-account"
                                    placeholder="Type to search users..."
                                    no-data-text="No users found"
                                    hide-no-data
                                >
                                    <template v-slot:selection="{ item }">
                                        <v-chip small :color="getAvatarColor(item.name)">
                                            <v-avatar left size="20">
                                                <span class="white--text" style="font-size: 11px;">
                                                    {{ getInitials(item.name) }}
                                                </span>
                                            </v-avatar>
                                            {{ item.name }}
                                        </v-chip>
                                    </template>
                                    <template v-slot:item="{ item }">
                                        <v-list-item-avatar :color="getAvatarColor(item.name)" size="32">
                                            <span class="white--text text-caption">
                                                {{ getInitials(item.name) }}
                                            </span>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                                        </v-list-item-content>
                                    </template>
                                </v-autocomplete>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="showBulkTaskDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        :loading="creatingBulkTasks"
                        :disabled="!allItemsHaveAssignees"
                        @click="createBulkTasks"
                        class="text-none"
                    >
                        <v-icon left>mdi-plus-circle-multiple</v-icon>
                        Create {{ checkedChecklistItems.length }} Task{{ checkedChecklistItems.length > 1 ? 's' : '' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import UnifiedTreeViewDashboard from "../modules/UnifiedTreeViewDashboard.vue";
import TaskCreationDialog from "../modules/TaskCreationDialog.vue";
import EditMeetingDialog from "./dialogs/EditMeetingDialog.vue";
import UploadMeetingResourceDialog from "./dialogs/UploadMeetingResourceDialog.vue";

export default {
    inject: ['users', 'companies', 'refreshMeetingTasks', 'findResourceById', 'refreshResources'],
    name: "ViewMeetingComponent",
    components: {
        UnifiedTreeViewDashboard,
        TaskCreationDialog,
        EditMeetingDialog,
        UploadMeetingResourceDialog,
    },
    data: () => ({
        loading: false,
        app_url: null,
        api_url: null,
        meeting_id: null,
        meeting: null,
        users_to_add: [],
        edit_meeting_dialog: false,
        meeting_tasks: [],
        create_task_dialog: false,
        selectedUserId: null,
        meetingStatusFilters: [],
        activeTab: 0, // 0 = Meeting Tasks, 1 = Resources, 2 = Video
        showUploadResourceDialog: false,
        meetingResources: [], // Resources attached to this meeting via pivot
        loadingRecording: false,
        allRecordings: [], // Array of all recordings for this meeting
        selectedRecordingId: null, // Currently selected recording ID
        recordingData: null,
        recordingError: null,
        loadingTranscript: false,
        transcriptData: null,
        currentVideoTime: 0,
        transcriptWords: [],
        lastScrolledSegmentIndex: -1,
        regeneratingSummary: false,
        summaryStatusPollingInterval: null,
        sendingSummaryEmail: false,
        extractedChecklistItems: [],
        summaryPanelOpen: 0, // Open by default (0 = first panel)
        showBulkTaskDialog: false,
        creatingBulkTasks: false,
        parsedSummary: [],
        showManualTranscriptDialog: false,
        manualTranscriptText: '',
        generatingManualSummary: false,
        manualTranscriptSummary: null,
        editingSummary: false,
        editedSummaryContent: '',
        savingSummary: false,
        recordingPollingInterval: null, // Polling timer for recording status
        deletingRecording: false,
        showDeleteRecordingDialog: false,
    }),
    created() {
        this.app_url = process.env.VUE_APP_URL;
        this.api_url = process.env.VUE_APP_API_URL;
    },
    mounted() {
        this.meeting_id = parseInt(this.$route.params.id);
        document.title = "DG Tasks - Meeting Details";
        this.initializeComponent();
        this.initializeMeetingStatusFilters();
        
        // Add event listener for checkboxes in summary after DOM updates
        this.$nextTick(() => {
            this.attachCheckboxListeners();
        });
    },
    beforeDestroy() {
        this.stopRecordingPolling();
        this.stopSummaryStatusPolling();
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_selected_company: "companies/get_selected_company",
            get_priorities: "tasks/get_priorities",
            get_statuses: "tasks/get_statuses",
            get_meeting_statuses: "meetings/get_meeting_statuses",
        }),

        canEditMeeting() {
            if (!this.meeting) return false;
            // Meeting owner or admin/manager can edit
            return this.meeting.user_id === this.get_user.id || this.get_user.role_id < 3 || this.get_user.role_id == 11;
        },

        availableUsers() {
            // If no users loaded yet, return empty array
            const allUsers = this.users() || [];
            if (!allUsers.length) {
                return [];
            }

            // Return all users (don't filter out attendees)
            return allUsers;
        },

        // Convert grouped tasks from store to flat array for UnifiedTreeViewDashboard
        allMeetingTasks() {
            if (!this.meeting_tasks || !Array.isArray(this.meeting_tasks)) {
                return [];
            }

            // Flatten the grouped structure
            return this.meeting_tasks.reduce((allTasks, group) => {
                if (group.tasks && Array.isArray(group.tasks)) {
                    allTasks.push(...group.tasks);
                }
                return allTasks;
            }, []);
        },

        statusFilterOptions() {
            const statuses = this.get_statuses || [];
            return statuses.filter(s => s.id !== 8);
        },

        filteredTasks() {
            let tasks = this.allMeetingTasks;

            // Filter by status
            if (this.meetingStatusFilters && this.meetingStatusFilters.length > 0) {
                tasks = tasks.filter(task => this.meetingStatusFilters.includes(task.status_id));
            }

            // Filter by selected user if any
            if (this.selectedUserId) {
                tasks = tasks.filter(task => {
                    // Check if task is assigned to the selected user
                    if (task.assignees && Array.isArray(task.assignees)) {
                        return task.assignees.some(assignee => assignee.user_id === this.selectedUserId);
                    }
                    // Fallback to assignee_id for tasks without assignees array
                    return task.assignee_id === this.selectedUserId;
                });
            }

            return tasks;
        },

        uncheckedChecklistItems() {
            const items = this.extractedChecklistItems.filter(item => !item.checked);
            console.log('Unchecked items for display:', items);
            return items;
        },

        checkedChecklistItems() {
            const checked = this.parsedSummary.filter(item => 
                item.type === 'checkbox' && 
                item.userChecked && 
                !item.alreadyCompleted
            );
            console.log('Checked items count:', checked.length);
            console.log('All parsed summary:', this.parsedSummary.map(s => ({ type: s.type, userChecked: s.userChecked, alreadyCompleted: s.alreadyCompleted, text: s.text?.substring(0, 50) })));
            return checked;
        },

        allItemsHaveAssignees() {
            return this.checkedChecklistItems.length > 0 && 
                   this.checkedChecklistItems.every(item => item.selectedAssignee);
        },

        userFilterOptions() {
            const allUsersOption = { id: null, name: 'All Users' };
            const userOptions = this.availableUsers.map(user => ({
                id: user.id,
                name: user.name
            }));
            return [allUsersOption, ...userOptions];
        },

        isRecordingProcessing() {
            if (!this.recordingData) return false;
            // Bot joined but never produced a recording (no recording_id from Recall.ai)
            if (!this.recordingData.recording_id) return false;
            const status = this.getCurrentRecordingStatus().toLowerCase();
            // Fatal errors mean processing completely failed — stop showing banner
            if (status === 'fatal' || status === 'error' || status === 'failed') return false;
            // If we already have a transcript summary, AI processing is complete
            if (this.transcriptData && this.transcriptData.summary) return false;
            // If analysis_done but transcript has finished loading with no summary, give up
            if (status === 'analysis_done' && !this.loadingTranscript) return false;
            // Otherwise still processing ('done'/'recording_done' means video ready but summary not yet)
            return true;
        },

        recordingSelectOptions() {
            return this.allRecordings.map((recording, index) => {
                let label = '';
                
                // Use created_at for recording timestamp
                const createdDate = this.$date(recording.created_at);
                const dateLabel = createdDate.format('MMMM DD, YYYY');
                const recordingTime = createdDate.format(this.$tz.time);
                
                // Recording number (reverse order - latest = #1)
                const recordingNumber = this.allRecordings.length - index;
                
                // Format: "Recording #6 - December 19, 2025 at 09:22 AM"
                label = `Recording #${recordingNumber} - ${dateLabel} at ${recordingTime}`;
                
                // Status is now a string in the new API format
                const status = recording.status || null;
                
                return {
                    id: recording.db_id, // Use db_id from API response
                    label: label,
                    status: status,
                    created_at: recording.created_at
                };
            });
        },

        meetingAttachments() {
            if (!this.meeting?.attachments) return [];

            // If attachments is already an array of objects, return it
            if (Array.isArray(this.meeting.attachments) &&
                this.meeting.attachments.length > 0 &&
                typeof this.meeting.attachments[0] === 'object') {
                return this.meeting.attachments;
            }

            // If attachments is an array of IDs, fetch resource details from injection
            if (Array.isArray(this.meeting.attachments)) {
                return this.meeting.attachments
                    .map(attachmentId => this.findResourceById(attachmentId))
                    .filter(resource => resource !== undefined);
            }

            // If attachments is a JSON string, parse it
            if (typeof this.meeting.attachments === 'string') {
                try {
                    const parsed = JSON.parse(this.meeting.attachments);
                    if (Array.isArray(parsed)) {
                        return parsed
                            .map(attachmentId => this.findResourceById(attachmentId))
                            .filter(resource => resource !== undefined);
                    }
                } catch (e) {
                    console.error('Error parsing meeting attachments:', e);
                }
            }

            return [];
        },
    },
    watch: {
        'transcriptData.summary': {
            handler(newSummary) {
                if (newSummary) {
                    this.parseSummaryToSections();
                }
            },
            immediate: false
        },
        activeTab(newVal) {
            // When user switches to Video tab (index 2), fetch recording
            if (newVal === 2 && !this.recordingData && !this.loadingRecording) {
                this.fetchRecordingUrl();
            }
        }
    },
    methods: {
        parseSummaryToSections() {
            if (!this.transcriptData || !this.transcriptData.summary) {
                console.log('No transcript data or summary');
                this.parsedSummary = [];
                return;
            }
            
            // Parse HTML using DOM
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = this.transcriptData.summary;
            
            const sections = [];
            const allCheckboxes = tempDiv.querySelectorAll('input[type="checkbox"]');
            
            console.log('Parsing summary - Found checkboxes:', allCheckboxes.length);
            
            // Extract all checkbox items with their section context
            allCheckboxes.forEach((checkbox, idx) => {
                const parent = checkbox.closest('li') || checkbox.parentElement;
                if (!parent) return;
                
                // Get text content
                const clone = parent.cloneNode(true);
                const cloneCheckbox = clone.querySelector('input[type="checkbox"]');
                if (cloneCheckbox) cloneCheckbox.remove();
                const text = clone.textContent.trim();
                
                // Check if already completed from backend
                const isChecked = checkbox.getAttribute('checked') !== null;
                
                // Check if this checklist item already exists as a task
                let matchedTask = false;
                if (this.allMeetingTasks && this.allMeetingTasks.length > 0) {
                    const normalizedText = text.trim().toLowerCase().replace(/\s+/g, ' ');
                    matchedTask = this.allMeetingTasks.some(task => {
                        const normalizedTaskName = (task.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
                        const isMatch = normalizedTaskName === normalizedText;
                        if (isMatch) {
                            console.log('✓ Task match found:', task.name);
                        }
                        return isMatch;
                    });
                }
                
                if (matchedTask) {
                    console.log('Disabling checkbox - task already exists:', text.substring(0, 50));
                }
                
                // Find the nearest heading to determine the assignee
                let currentElement = parent;
                let suggestedName = null;
                
                // Walk up the DOM to find a heading (h1-h6) or div with specific text pattern
                while (currentElement && currentElement !== tempDiv) {
                    currentElement = currentElement.previousElementSibling || currentElement.parentElement;
                    
                    if (currentElement) {
                        const elementText = currentElement.textContent || '';
                        
                        // Check for pattern: "Action items for [Name]"
                        const match = elementText.match(/Action items for\s+(.+)/i);
                        if (match && match[1]) {
                            suggestedName = match[1].trim();
                            console.log('Suggesting task for:', suggestedName);
                            break;
                        }
                    }
                }
                
                sections.push({
                    type: 'checkbox',
                    alreadyCompleted: isChecked || matchedTask, // Mark as completed if already exists as task
                    userChecked: false,
                    text: text,
                    selectedAssignee: null, // Don't auto-populate
                    suggestedFor: suggestedName, // Store the suggestion as text
                    checkboxIndex: idx
                });
            });
            
            console.log('Parsed sections:', sections.length);
            console.log('Tasks matched:', sections.filter(s => s.alreadyCompleted).length);
            this.parsedSummary = sections;
            
            // Attach listeners after Vue updates DOM
            this.$nextTick(() => {
                this.attachCheckboxListeners();
            });
        },
        
        attachCheckboxListeners() {
            // Find all checkboxes in the rendered HTML
            const container = this.$el.querySelector('.summary-content');
            if (!container) return;
            
            const checkboxes = container.querySelectorAll('input[type="checkbox"]');
            console.log('Attaching listeners to', checkboxes.length, 'checkboxes');
            console.log('Parsed summary count:', this.parsedSummary.length);
            console.log('Already completed count:', this.parsedSummary.filter(s => s.alreadyCompleted).length);
            
            checkboxes.forEach((checkbox, idx) => {
                const section = this.parsedSummary.find(s => s.checkboxIndex === idx);
                
                // Disable if already completed (from backend or matched task)
                if (section && section.alreadyCompleted) {
                    checkbox.checked = true;
                    checkbox.disabled = true;
                    checkbox.setAttribute('checked', 'checked');
                    console.log('✓ Disabling checkbox', idx, '- already completed:', section.text.substring(0, 50));
                    return;
                }
                
                // Skip if already has checked attribute from HTML
                if (checkbox.hasAttribute('checked')) {
                    checkbox.disabled = true;
                    return;
                }
                
                // Add change listener
                checkbox.addEventListener('change', (e) => {
                    if (section && !section.alreadyCompleted) {
                        section.userChecked = e.target.checked;
                        console.log('Checkbox', idx, 'changed to', e.target.checked, 'for:', section.text.substring(0, 50));
                    }
                });
            });
        },
        initializeMeetingStatusFilters() {
            const statuses = this.get_statuses || [];
            if (!statuses.length) {
                // Statuses not loaded yet — watch and retry once
                const unwatch = this.$watch('get_statuses', (val) => {
                    if (val && val.length) {
                        this._setDefaultMeetingStatusFilters(val);
                        unwatch();
                    }
                });
                return;
            }
            this._setDefaultMeetingStatusFilters(statuses);
        },

        _setDefaultMeetingStatusFilters(statuses) {
            const completedStatus = statuses.find(s => s.name && s.name.toLowerCase().trim() === 'completed');
            this.meetingStatusFilters = statuses
                .filter(s => {
                    if (s.id === 8) return false; // exclude For Approval
                    if (completedStatus && s.id === completedStatus.id) return false; // exclude Completed
                    return true;
                })
                .map(s => s.id);
        },

        async initializeComponent() {
            this.loading = true;
            try {
                // Only block render on the critical meeting data
                await this.getMeeting();
            } catch (error) {
                console.error('Error initializing meeting view:', error);
                this.$toast.error('Failed to load meeting data');
            } finally {
                // Show the page immediately once meeting data is ready
                this.loading = false;
            }

            // Load secondary data after the page is already visible (non-blocking)
            Promise.allSettled([
                this.getTasks(),
                this.loadMeetingResources(),
                // fetchRecordingUrl is triggered by the activeTab watcher when user opens Video tab
                // loadResources() skipped — resources already provided by DefaultLayout
            ]).then(results => {
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        console.error(`Background load failed [${index}]:`, result.reason);
                    }
                });
            });
        },

        async getMeeting() {
            try {
                const response = await this.$axios.get(`api/meetings/get_meeting/${this.meeting_id}`);
                if (response.data.response) {
                    this.meeting = response.data.data;
                } else {
                    throw new Error(response.data.message || 'Failed to load meeting');
                }
            } catch (error) {
                console.error('Error fetching meeting:', error);
                this.$toast.error('Failed to load meeting details');
                throw error;
            }
        },

        async loadResources() {
            // Resources are now loaded by DefaultLayout via provider pattern
            // No need to dispatch to store, just ensure they're loaded
            try {
                if (typeof this.refreshResources === 'function') {
                    await this.refreshResources();
                }
            } catch (error) {
                console.error('Error loading resources:', error);
                // Don't show error toast as this is not critical for meeting view
            }
        },

        async getTasks() {
            try {
                const response = await this.$axios.get(`api/tasks/get_meeting_task/${this.meeting_id}`);

                if (response.data.response) {
                    const tasks = response.data.data || [];

                    // Group tasks by company as expected by the store
                    this.meeting_tasks = tasks.reduce((acc, task) => {
                        const group = acc.find(g => g.company_id === task.company_id);

                        if (group) {
                            group.tasks.push(task);
                        } else {
                            acc.push({
                                company_id: task.company_id,
                                name: task.company?.name || "Unknown Company",
                                tasks: [task],
                            });
                        }

                        return acc;
                    }, []);
                    
                    // Re-parse summary after tasks are loaded to update checkbox states
                    if (this.transcriptData && this.transcriptData.summary) {
                        this.$nextTick(() => {
                            this.parseSummaryToSections();
                        });
                    }
                } else {
                    console.error('API error:', response.data.message);
                    this.$toast.error(response.data.message || 'Failed to load meeting tasks');
                    this.meeting_tasks = []; // Set empty array to prevent stuck loading
                }
            } catch (error) {
                console.error('Error fetching meeting tasks:', error);
                this.$toast.error('Failed to load meeting tasks');
                this.meeting_tasks = []; // Set empty array to prevent stuck loading
            }
        },

        toggleEditMode() {
            this.edit_meeting_dialog = true;
        },

        async handleUpdateMeeting(payload) {
            try {
                // Validate conference link format if provided
                const urlPattern = /^(https?:\/\/)[^\s]+$/i;
                if (payload.conference_link && payload.conference_link.trim() && !urlPattern.test(payload.conference_link.trim())) {
                    this.$toast.error('Conference link must be a valid URL (e.g. https://meet.google.com/...)');
                    return;
                }

                // Store original conference link to check if it changed
                const originalConferenceLink = this.meeting.conference_link;
                const newConferenceLink = payload.conference_link;
                
                const response = await this.$axios.post(`api/meetings/update_meeting/${this.meeting.id}`, payload);

                if (response.data.response) {
                    this.$toast.success(response.data.message);
                    
                    // Check if conference link was added or changed
                    const conferenceLinkenChanged = newConferenceLink && 
                        newConferenceLink.trim() !== '' && 
                        newConferenceLink !== originalConferenceLink;
                    
                    // Trigger recording bot if conference link is new or changed
                    if (conferenceLinkenChanged) {
                        await this.startRecordingBot(this.meeting.id);
                    }
                    
                    // Refresh meeting data
                    await this.getMeeting();
                } else {
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error updating meeting:', error);
                this.$toast.error('Failed to update meeting');
            }
        },

        async startRecordingBot(meetingId) {
            try {
                this.$toast.info('Starting recording bot...');
                const response = await this.$axios.post(`api/meetings/${meetingId}/start-recording`, {
                    bot_name: 'DG VENTURES Note taker'
                });
                
                if (response.data.response) {
                    this.$toast.success('Recording bot started successfully');
                } else {
                    const message = response.data.message || 'Failed to start recording bot';
                    const botData = response.data.data;

                    if (message === 'A note taker is already running in this meeting') {
                        const statusLabel = botData?.status
                            ? botData.status.replace(/_/g, ' ')
                            : 'active';
                        this.$toast.error(`A note taker is already running in this meeting (${statusLabel})`);
                    } else {
                        console.warn('Failed to start recording bot:', message);
                    }
                }
            } catch (error) {
                // Don't show error to user as meeting was updated successfully
                console.error('Error starting recording bot:', error);
            }
        },

        handleJoinMeeting() {
            // Only trigger recording bot if current user is the meeting creator
            if (this.meeting.user_id === this.get_user.id) {
                this.startRecordingBot(this.meeting.id);
            }
        },

        canRemoveAttendee(userId) {
            // Don't allow removing the meeting creator
            return userId !== this.meeting.user_id;
        },

        async removeAttendee(access) {
            if (!this.canRemoveAttendee(access.user_id)) return;

            // Store original state for potential rollback
            const originalAccess = [...this.meeting.access];

            try {
                // Optimistic update - immediately remove from UI
                const accessIndex = this.meeting.access.findIndex(a => a.id === access.id);
                if (accessIndex !== -1) {
                    this.meeting.access.splice(accessIndex, 1);
                }

                // Show immediate success feedback
                this.$toast.success('Attendee removed successfully');

                // Background API call (don't await)
                this.$axios.delete(`api/meetings/delete_meeting_access/${access.id}`)
                    .then(response => {
                        if (response.data.response) {
                            console.log('Remove attendee API call successful');
                            // Optionally refresh meeting data to sync with server
                            this.getMeeting().catch(console.error);
                        } else {
                            // API failed - rollback optimistic changes
                            this.meeting.access = originalAccess;
                            this.$toast.error(response.data.message || 'Failed to remove attendee');
                        }
                    })
                    .catch(error => {
                        // Network error - rollback optimistic changes
                        console.error('Error removing attendee:', error);
                        this.meeting.access = originalAccess;
                        this.$toast.error('Failed to remove attendee - change has been reverted');
                    });

            } catch (error) {
                // Immediate error - rollback optimistic changes
                console.error('Error in optimistic attendee removal:', error);
                this.meeting.access = originalAccess;
                this.$toast.error('Failed to remove attendee');
            }
        },

        openCreateTaskDialog() {
            this.create_task_dialog = true;
        },

        closeCreateTaskDialog() {
            this.create_task_dialog = false;
        },

        onTasksCreated() {
            // Refresh tasks when new ones are created
            this.getTasks();
        },

        updateTaskData({ taskId, updatedData }) {
            // Update task in local meeting_tasks array
            if (!this.meeting_tasks || !Array.isArray(this.meeting_tasks)) return;
            
            for (const group of this.meeting_tasks) {
                if (group.tasks && Array.isArray(group.tasks)) {
                    const taskIndex = group.tasks.findIndex(task => task.id === taskId);
                    if (taskIndex !== -1) {
                        Object.assign(group.tasks[taskIndex], updatedData);
                        break;
                    }
                }
            }
        },

        handleOptimisticUpdate({ type, taskData, tempId, realTask }) {
            // Handle optimistic updates from UnifiedTreeViewDashboard
            if (type === 'create') {
                // Add the new task to the local meeting_tasks array
                this.addTaskToMeetingTasks(taskData);
            } else if (type === 'replace') {
                // Replace temporary task with real task from API
                console.log(`[ViewMeeting] Replacing temp task ${tempId} with real task ${realTask.id}`);
                this.removeTaskFromMeetingTasks(tempId);
                this.addTaskToMeetingTasks(realTask);
            } else if (type === 'update') {
                // Sync the updated task fields into the local meeting_tasks array
                if (taskData && taskData.id) {
                    this.updateTaskData({ taskId: taskData.id, updatedData: taskData });
                }
            } else if (type === 'delete') {
                // Remove task from local meeting_tasks array
                this.removeTaskFromMeetingTasks(taskData.id);
            }
        },

        addTaskToMeetingTasks(task) {
            if (!this.meeting_tasks || !Array.isArray(this.meeting_tasks)) {
                this.meeting_tasks = [];
            }

            // Find or create the group for this task's company
            const companyId = task.company_id;
            let group = this.meeting_tasks.find(g => g.company_id === companyId);

            if (!group) {
                // Create new group
                group = {
                    company_id: companyId,
                    name: task.company?.name || "Unknown Company",
                    tasks: []
                };
                this.meeting_tasks.push(group);
            }

            // Add task to the group (at the beginning for newest first)
            group.tasks.unshift(task);
        },

        removeTaskFromMeetingTasks(taskId) {
            if (!this.meeting_tasks || !Array.isArray(this.meeting_tasks)) return;

            for (const group of this.meeting_tasks) {
                if (group.tasks && Array.isArray(group.tasks)) {
                    const taskIndex = group.tasks.findIndex(task => task.id === taskId);
                    if (taskIndex !== -1) {
                        group.tasks.splice(taskIndex, 1);
                        // Remove empty groups
                        if (group.tasks.length === 0) {
                            const groupIndex = this.meeting_tasks.indexOf(group);
                            this.meeting_tasks.splice(groupIndex, 1);
                        }
                        break;
                    }
                }
            }
        },

        // Helper methods
        getMeetingStatusName() {
            if (!this.meeting?.meeting_status_id || !this.get_meeting_statuses) return 'Unknown';
            const status = this.get_meeting_statuses.find(s => s.id === this.meeting.meeting_status_id);
            return status?.name || 'Unknown';
        },

        getMeetingStatusColor() {
            if (!this.meeting?.meeting_status_id || !this.get_meeting_statuses) return 'grey';
            const status = this.get_meeting_statuses.find(s => s.id === this.meeting.meeting_status_id);
            return status?.color || 'grey';
        },

        getAvatarColor(name) {
            const colors = [
                'primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info',
                'purple', 'pink', 'indigo', 'cyan', 'teal', 'green', 'orange', 'brown'
            ];
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },

        getInitials(name) {
            if (!name) return 'U';
            return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
        },

        // New helper methods for enhanced meeting display
        formatMeetingDate(dateString) {
            if (!dateString) return 'No date set';
            return this.$date(dateString).format("MMM DD, YYYY");
        },

        formatMeetingTimeRange(meetingDate, startTime, endTime) {
            if (!startTime && !endTime) return 'No time set';
            // Converted as a pair with the date, since a zone shift can roll it.
            const start = startTime
                ? this.$tz.showTime(this.$tz.splitLocalDateTime(meetingDate, startTime).time)
                : null;
            const end = endTime
                ? this.$tz.showTime(this.$tz.splitLocalDateTime(meetingDate, endTime).time)
                : null;
            if (start && end) return `${start} - ${end}`;
            if (start) return `Starts at ${start}`;
            return `Ends at ${end}`;
        },

        getCreatorName(meeting) {
            // Check if creator info is available through relationships
            if (meeting.creator && meeting.creator.name) {
                return meeting.creator.name;
            }

            // Fallback to finding in access list by creator_id
            if (meeting.creator_id && meeting.access) {
                const creator = meeting.access.find(access => access.user_id === meeting.creator_id);
                if (creator && creator.user) {
                    return creator.user.name;
                }
            }

            // Fallback to original user_id behavior
            if (meeting.user_id && meeting.access) {
                const creator = meeting.access.find(access => access.user_id === meeting.user_id);
                if (creator && creator.user) {
                    return creator.user.name;
                }
            }

            return null;
        },

        getNoteTakerName(meeting) {
            // Check if note taker info is available through relationships
            if (meeting.note_taker && meeting.note_taker.name) {
                return meeting.note_taker.name;
            }

            // Fallback to finding in access list by note_taker_id
            if (meeting.note_taker_id && meeting.access) {
                const noteTaker = meeting.access.find(access => access.user_id === meeting.note_taker_id);
                if (noteTaker && noteTaker.user) {
                    return noteTaker.user.name;
                }
            }

            return null;
        },

        getMeetingFrequencyColor(frequency) {
            switch (frequency) {
                case 'once':
                    return 'primary';
                case 'recurring':
                    return 'success';
                default:
                    return 'grey';
            }
        },

        formatRecurrenceDays(days) {
            if (!days || !Array.isArray(days) || days.length === 0) return 'No days selected';
            
            const dayNames = {
                0: 'Sun',
                1: 'Mon', 
                2: 'Tue',
                3: 'Wed',
                4: 'Thu',
                5: 'Fri',
                6: 'Sat'
            };
            
            const sortedDays = [...days].sort((a, b) => a - b);
            const dayLabels = sortedDays.map(day => dayNames[day] || 'Unknown');
            
            if (dayLabels.length <= 2) {
                return dayLabels.join(' and ');
            } else if (dayLabels.length === 7) {
                return 'Every day';
            } else {
                return dayLabels.slice(0, -1).join(', ') + ' and ' + dayLabels[dayLabels.length - 1];
            }
        },

        // Attachment handling methods
        async downloadAttachment(attachment) {
            try {
                const response = await this.$axios.get(`/api/resources/download/${attachment.id}`, {
                    responseType: 'blob'
                });

                // Create blob link to download
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', attachment.name);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.$toast.success('Download started');
            } catch (error) {
                console.error('Download failed:', error);
                this.$toast.error('Failed to download attachment');
            }
        },

        ensureExternalUrl(url) {
            if (!url) return '#';
            // If it already has a protocol, return as is
            if (url.startsWith('http://') || url.startsWith('https://')) {
                return url;
            }
            // Add https:// protocol by default for external links
            return `https://${url}`;
        },

        getAttachmentViewUrl(url) {
            // If it's already a full URL, return it
            if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                return url;
            }

            // Otherwise, construct the URL using the API base
            return `${this.api_url || process.env.VUE_APP_API_URL}/storage/${url}`;
        },

        getFileTypeIcon(fileType) {
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                image: 'mdi-file-image-box',
                video: 'mdi-file-video-box',
                audio: 'mdi-file-music-box'
            };
            return iconMap[fileType] || 'mdi-file-document-outline';
        },

        getFileTypeColor(fileType) {
            const colorMap = {
                pdf: 'red',
                doc: 'blue',
                docx: 'blue',
                xls: 'green',
                xlsx: 'green',
                ppt: 'orange',
                pptx: 'orange',
                txt: 'grey',
                zip: 'purple',
                image: 'pink',
                video: 'deep-purple',
                audio: 'teal'
            };
            return colorMap[fileType] || 'grey';
        },

        // Meeting Resources Management Methods
        async loadMeetingResources() {
            try {
                const response = await this.$axios.get(`/api/meetings/${this.meeting_id}/resources`);
                if (response.data.response || response.data.success) {
                    this.meetingResources = response.data.data || [];
                } else {
                    console.error('Failed to load meeting resources:', response.data.message);
                }
            } catch (error) {
                console.error('Error loading meeting resources:', error);
                this.$toast.error('Failed to load meeting resources');
            }
        },

        async handleResourceUploaded(resource) {
            // Resource was uploaded and automatically attached to meeting
            // Add it to the local meetingResources array
            this.meetingResources.unshift(resource);
            
            // Optionally refresh resources in DefaultLayout to update Resource Library
            if (typeof this.refreshResources === 'function') {
                this.refreshResources().catch(console.error);
            }
        },

        async detachResource(resourceId) {
            if (!confirm('Are you sure you want to remove this resource from the meeting?')) {
                return;
            }

            try {
                const response = await this.$axios.post(`/api/meetings/${this.meeting_id}/detach-resource`, {
                    resource_id: resourceId
                });

                if (response.data.response || response.data.success) {
                    this.$toast.success('Resource removed successfully');
                    // Remove from local array
                    const index = this.meetingResources.findIndex(r => r.id === resourceId);
                    if (index !== -1) {
                        this.meetingResources.splice(index, 1);
                    }
                } else {
                    throw new Error(response.data.message || 'Failed to remove resource');
                }
            } catch (error) {
                console.error('Error detaching resource:', error);
                this.$toast.error(error.message || 'Failed to remove resource');
            }
        },

        async downloadResource(resource) {
            // For URL resources, open the URL directly
            if (resource.type === 'url' || (resource.url && !resource.file_path)) {
                window.open(resource.url, '_blank');
                this.$toast.success(`Opened ${resource.name}`);
                return;
            }

            // For file resources, download them
            try {
                const apiBaseUrl = process.env.VUE_APP_API_URL || '';
                const downloadUrl = `${apiBaseUrl}/api/resources/${resource.id}/download`;

                const response = await this.$axios.get(downloadUrl, {
                    responseType: 'blob'
                });

                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', resource.name);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.$toast.success(`Downloaded ${resource.name}`);
            } catch (error) {
                console.error('Download failed:', error);
                this.$toast.error('Failed to download resource');
            }
        },

        async fetchRecordingUrl() {
            this.loadingRecording = true;
            this.recordingError = null;

            try {
                // Fetch all recordings for this meeting (supports multiple occurrences)
                const response = await this.$axios.get(`api/meetings/${this.meeting_id}/recordings`);

                if (response.data.response) {
                    const data = response.data.data;
                    
                    // Check if response is array (multiple recordings) or single object
                    if (Array.isArray(data)) {
                        this.allRecordings = data;
                        
                        if (this.allRecordings.length > 0) {
                            // Select the most recent recording by default
                            const latestRecording = this.allRecordings[0];
                            this.selectedRecordingId = latestRecording.db_id; // Use db_id
                            this.recordingData = latestRecording;

                            // Populate summary from recording if present (e.g. manual transcript)
                            if (latestRecording.summary) {
                                if (!this.transcriptData) this.transcriptData = {};
                                this.transcriptData.summary = latestRecording.summary;
                                this.manualTranscriptSummary = latestRecording.summary;
                            }
                            
                            // Automatically fetch transcript if available
                            if (this.getTranscriptId()) {
                                this.fetchTranscript();
                            }
                        } else {
                            this.recordingError = 'No recordings available';
                        }
                    } else {
                        // Single recording (backward compatibility)
                        this.allRecordings = [data];
                        this.selectedRecordingId = data.db_id || data.id; // Use db_id or fallback to id
                        this.recordingData = data;

                        // Populate summary from recording if present (e.g. manual transcript)
                        if (data.summary) {
                            if (!this.transcriptData) this.transcriptData = {};
                            this.transcriptData.summary = data.summary;
                            this.manualTranscriptSummary = data.summary;
                        }
                        
                        // Automatically fetch transcript if available
                        if (this.getTranscriptId()) {
                            this.fetchTranscript();
                        }
                    }
                } else {
                    this.recordingError = response.data.message || 'No recording available';
                }
            } catch (error) {
                console.error('Error fetching recording:', error);
                this.recordingError = error.response?.data?.message || 'Failed to load recording';
            } finally {
                this.loadingRecording = false;
                // Start polling if recording is still processing
                this.checkAndStartPolling();
            }
        },

        startRecordingPolling() {
            // Don't start if already polling
            if (this.recordingPollingInterval) return;
            
            console.log('[Recording Polling] Starting polling every 10 seconds...');
            this.recordingPollingInterval = setInterval(async () => {
                console.log('[Recording Polling] Checking recording status...');
                try {
                    const response = await this.$axios.get(`api/meetings/${this.meeting_id}/recordings`);
                    
                    if (response.data.response) {
                        const data = response.data.data;
                        const recordings = Array.isArray(data) ? data : [data];
                        
                        if (recordings.length > 0) {
                            this.allRecordings = recordings;
                            
                            // Update currently selected recording
                            const currentRecording = recordings.find(r => r.db_id === this.selectedRecordingId) || recordings[0];
                            this.recordingData = currentRecording;
                            this.selectedRecordingId = currentRecording.db_id;
                            
                            // If recording now has summary data populated
                            if (currentRecording.summary) {
                                if (!this.transcriptData) this.transcriptData = {};
                                this.transcriptData.summary = currentRecording.summary;
                                this.manualTranscriptSummary = currentRecording.summary;
                            }
                            
                            // If recording is now done, stop polling and fetch transcript
                            if (!this.isRecordingProcessing) {
                                console.log('[Recording Polling] Recording is ready! Stopping polling.');
                                this.stopRecordingPolling();
                                this.$toast.success('Meeting recording is ready!');

                                // Fetch transcript if available
                                if (this.getTranscriptId()) {
                                    this.fetchTranscript();
                                }
                            } else if (this.getTranscriptId() && !currentRecording.summary && !this.loadingTranscript) {
                                // A transcript exists but no summary yet - fetchTranscript() is what
                                // actually triggers first-time AI generation server-side (see
                                // RecallAiController::getTranscript's auto-generate-when-empty logic).
                                // isRecordingProcessing can never flip false via polling alone since it
                                // depends on transcriptData.summary, which only fetchTranscript() sets -
                                // without this, the poll just spins forever waiting for a fetch it never makes.
                                console.log('[Recording Polling] Transcript ready, no summary yet - triggering generation...');
                                this.fetchTranscript();
                            }
                        }
                    }
                } catch (error) {
                    console.error('[Recording Polling] Error:', error);
                }
            }, 10000); // Poll every 10 seconds
        },

        stopRecordingPolling() {
            if (this.recordingPollingInterval) {
                console.log('[Recording Polling] Stopping polling.');
                clearInterval(this.recordingPollingInterval);
                this.recordingPollingInterval = null;
            }
        },

        checkAndStartPolling() {
            if (this.isRecordingProcessing) {
                this.startRecordingPolling();
            } else {
                this.stopRecordingPolling();
            }
        },

        loadSelectedRecording() {
            // When user selects a different recording occurrence
            const selected = this.allRecordings.find(r => r.db_id === this.selectedRecordingId);
            
            if (selected) {
                this.recordingData = selected;
                this.transcriptData = null; // Clear previous transcript
                this.currentVideoTime = 0;
                
                // Populate summary from selected recording if present
                if (selected.summary) {
                    if (!this.transcriptData) this.transcriptData = {};
                    this.transcriptData.summary = selected.summary;
                    this.manualTranscriptSummary = selected.summary;
                } else {
                    this.manualTranscriptSummary = null;
                }
                
                // Reset and reload video player
                this.$nextTick(() => {
                    if (this.$refs.videoPlayer) {
                        this.$refs.videoPlayer.currentTime = 0;
                        this.$refs.videoPlayer.load(); // Force video to reload new source
                    }
                });
                
                // Fetch transcript for this recording
                if (this.getTranscriptId()) {
                    this.fetchTranscript();
                }
            }
        },

        getCurrentRecordingStatus() {
            if (!this.recordingData) return 'Unknown';
            
            // Handle string status (new API format)
            if (typeof this.recordingData.status === 'string') {
                return this.recordingData.status;
            }
            
            // Handle array status (old format - backward compatibility)
            if (Array.isArray(this.recordingData.status) && this.recordingData.status.length > 0) {
                const latestStatus = this.recordingData.status[this.recordingData.status.length - 1];
                return latestStatus?.code || 'Unknown';
            }
            
            return 'Unknown';
        },

        getCurrentRecordingStatusColor() {
            const status = this.getCurrentRecordingStatus();
            const statusLower = status.toLowerCase();
            
            if (statusLower === 'done' || statusLower === 'recording_done') return 'success';
            if (statusLower.includes('recording')) return 'warning';
            if (statusLower.includes('error') || statusLower.includes('failed')) return 'error';
            if (statusLower.includes('call_ended')) return 'info';
            return 'grey';
        },

        getTranscriptId() {
            // Check if recording has transcript information
            if (!this.recordingData) return null;
            
            // Check for direct transcript object with id
            if (this.recordingData.transcript?.id) {
                return this.recordingData.transcript.id;
            }
            
            // Check for direct transcript_id field (alternative format)
            if (this.recordingData.transcript_id) {
                return this.recordingData.transcript_id;
            }
            
            // // Legacy support: Check for raw_recording object
            // if (this.recordingData.raw_recording?.media_shortcuts?.transcript?.id) {
            //     return this.recordingData.raw_recording.media_shortcuts.transcript.id;
            // }
            
            return null;
        },

        getTranscriptUrl() {
            // Navigate through the nested structure to get transcript download URL
            if (!this.recordingData || !this.recordingData.raw_recording) return null;
            
            const shortcuts = this.recordingData.raw_recording.media_shortcuts;
            if (!shortcuts || !shortcuts.transcript) return null;
            
            return shortcuts.transcript.data?.download_url || null;
        },

        async fetchTranscript() {
            const transcriptId = this.getTranscriptId();
            if (!transcriptId) {
                console.log('No transcript ID available');
                return;
            }

            console.log('Fetching transcript with ID:', transcriptId);
            this.loadingTranscript = true;
            try {
                const response = await this.$axios.post(`api/meetings/${this.meeting_id}/transcript`, {
                    params: {
                        transcript_id: transcriptId
                    },
                    recording_id: this.selectedRecordingId // Pass recording_id to ensure we get the right recording
                });
                
                console.log('Transcript API response:', response.data);
                
                if (response.data.response) {
                    this.transcriptData = response.data.data;
                    console.log('Transcript data with AI summary:', this.transcriptData);
                    
                    // Parse summary sections (AI-generated summary from backend)
                    this.parseSummaryToSections();
                    
                    // Fetch the actual transcript words from download URL
                    await this.fetchTranscriptWords();
                } else {
                    console.error('Failed to load transcript:', response.data.message);
                    this.$toast.error(response.data.message || 'Failed to load transcript');
                }
            } catch (error) {
                console.error('Error fetching transcript:', error);
                this.$toast.error('Failed to load transcript');
            } finally {
                this.loadingTranscript = false;
            }
        },

        async fetchTranscriptWords() {
            if (!this.transcriptData || !this.transcriptData.download_url) {
                console.log('No download URL available:', this.transcriptData);
                return;
            }
            
            console.log('Fetching transcript words from:', this.transcriptData.download_url);
            
            try {
                // Fetch the JSON file from S3
                const response = await fetch(this.transcriptData.download_url);
                const data = await response.json();
                
                console.log('Transcript data:', data);
                
                // Recall AI structure: array of participant objects with words
                let allWords = [];
                
                if (Array.isArray(data)) {
                    // Iterate through each participant
                    data.forEach(participantData => {
                        if (participantData.participant && participantData.words && Array.isArray(participantData.words)) {
                            const speakerName = participantData.participant.name || 'Unknown';
                            
                            // Add each word with speaker info and flattened timestamps
                            participantData.words.forEach(word => {
                                allWords.push({
                                    text: word.text,
                                    speaker: speakerName,
                                    start_timestamp: word.start_timestamp?.relative || 0,
                                    end_timestamp: word.end_timestamp?.relative || 0,
                                    absolute_start: word.start_timestamp?.absolute,
                                    absolute_end: word.end_timestamp?.absolute
                                });
                            });
                        }
                    });
                    
                    // Sort by start timestamp
                    allWords.sort((a, b) => a.start_timestamp - b.start_timestamp);
                }
                
                if (allWords.length > 0) {
                    console.log('Processed', allWords.length, 'words from', data.length, 'participants');
                    console.log('Sample words:', allWords.slice(0, 3));
                    
                    // Store words on transcriptData
                    this.$set(this.transcriptData, 'words', allWords);
                    this.$toast.success(`Transcript loaded: ${allWords.length} words`);
                } else {
                    console.error('No words found in transcript data');
                    this.$toast.error('No transcript content found');
                }
            } catch (error) {
                console.error('Error fetching transcript words:', error);
                this.$toast.error('Failed to load transcript content');
            }
        },

        async submitManualTranscript() {
            if (!this.manualTranscriptText.trim()) {
                this.$toast.error('Please enter a transcript before generating a summary');
                return;
            }

            this.generatingManualSummary = true;
            try {
                this.$toast.info('Saving transcript and generating AI summary...');

                // Save manual_transcript on the recording and trigger AI summary generation
                const response = await this.$axios.post(
                    `api/meetings/${this.meeting_id}/recordings/manual-transcript`,
                    {
                        manual_transcript: this.manualTranscriptText.trim(),
                        recording_id: this.selectedRecordingId || null,
                    }
                );

                if (response.data.response || response.data.success) {
                    const data = response.data.data || {};

                    // Store the recording ID so regenerate/email actions work
                    const recordingId = data.id || data.db_id || data.recording_id;
                    if (recordingId) {
                        this.selectedRecordingId = recordingId;
                        // Add to allRecordings if not already present, otherwise update it
                        const existingIndex = this.allRecordings.findIndex(r => (r.db_id || r.id) === recordingId);
                        if (existingIndex === -1) {
                            this.allRecordings.push(data);
                        } else {
                            this.$set(this.allRecordings, existingIndex, data);
                        }
                    }

                    // Set as current recording so it becomes the selected occurrence
                    this.recordingData = data;

                    // Populate summary
                    const summary = data.summary;
                    if (summary) {
                        this.manualTranscriptSummary = summary;
                        if (!this.transcriptData) {
                            this.transcriptData = {};
                        }
                        this.transcriptData.summary = summary;
                    }

                    this.$toast.success(response.data.message || 'Transcript saved and AI summary generated successfully');
                    this.showManualTranscriptDialog = false;
                } else {
                    throw new Error(response.data.message || 'Failed to save transcript');
                }
            } catch (error) {
                console.error('Error saving manual transcript:', error);
                this.$toast.error(error.message || 'Failed to save transcript and generate summary');
            } finally {
                this.generatingManualSummary = false;
            }
        },

        async openEditTranscriptDialog() {
            // Use already-cached manual_transcript if available
            if (this.recordingData && this.recordingData.manual_transcript) {
                this.manualTranscriptText = this.recordingData.manual_transcript;
                this.showManualTranscriptDialog = true;
                return;
            }

            // Re-fetch from the recordings list endpoint (same one used on load)
            try {
                const response = await this.$axios.get(`api/meetings/${this.meeting_id}/recordings`);
                if (response.data.response || response.data.success) {
                    const data = response.data.data;
                    const recordings = Array.isArray(data) ? data : [data];
                    // Find the matching recording by db_id
                    const match = recordings.find(r => r.db_id === this.selectedRecordingId) || recordings[0];
                    if (match) {
                        this.manualTranscriptText = match.manual_transcript || '';
                        // Cache it so we don't re-fetch
                        if (this.recordingData) {
                            this.recordingData.manual_transcript = match.manual_transcript || '';
                        }
                    } else {
                        this.manualTranscriptText = '';
                    }
                } else {
                    this.manualTranscriptText = '';
                }
            } catch (error) {
                console.error('Error fetching transcript text:', error);
                this.manualTranscriptText = '';
            }
            this.showManualTranscriptDialog = true;
        },

        async regenerateSummaryFromManual() {
            if (!this.meeting_id || !this.selectedRecordingId) {
                this.$toast.error('Cannot regenerate summary - missing recording information');
                return;
            }
            this.regeneratingSummary = true;
            try {
                this.$toast.info('Regenerating AI summary from transcript...');
                const response = await this.$axios.post(
                    `api/meetings/${this.meeting_id}/recordings/${this.selectedRecordingId}/regenerate-summary-from-transcript`
                );
                if (response.data.response || response.data.success) {
                    const summary = response.data.data?.summary || response.data.summary;
                    if (summary) {
                        if (!this.transcriptData) this.transcriptData = {};
                        this.transcriptData.summary = summary;
                        this.manualTranscriptSummary = summary;
                    }
                    this.$toast.success('AI summary regenerated successfully');
                } else {
                    throw new Error(response.data.message || 'Failed to regenerate summary');
                }
            } catch (error) {
                console.error('Error regenerating summary from transcript:', error);
                this.$toast.error(error.message || 'Failed to regenerate summary');
            } finally {
                this.regeneratingSummary = false;
            }
        },

        deleteRecording() {
            this.showDeleteRecordingDialog = true;
        },

        async confirmDeleteRecording() {
            this.deletingRecording = true;
            try {
                const response = await this.$axios.delete(
                    `api/meetings/${this.meeting_id}/recordings/${this.selectedRecordingId}`
                );

                if (response.data.response) {
                    this.$toast.success('Recording deleted successfully');
                    // Remove from local array
                    this.allRecordings = this.allRecordings.filter(r => r.db_id !== this.selectedRecordingId);
                    // Select the next available recording, or clear
                    if (this.allRecordings.length > 0) {
                        this.selectedRecordingId = this.allRecordings[0].db_id;
                        this.loadSelectedRecording();
                    } else {
                        this.selectedRecordingId = null;
                        this.recordingData = null;
                        this.transcriptData = null;
                    }
                } else {
                    this.$toast.error(response.data.message || 'Failed to delete recording');
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to delete recording');
            } finally {
                this.deletingRecording = false;
                this.showDeleteRecordingDialog = false;
            }
        },

        startEditSummary() {
            this.editedSummaryContent = this.transcriptData ? this.transcriptData.summary : '';
            this.editingSummary = true;
        },

        cancelEditSummary() {
            this.editingSummary = false;
            this.editedSummaryContent = '';
        },

        async saveEditedSummary() {
            if (!this.selectedRecordingId) {
                this.$toast.error('Cannot save summary - missing recording information');
                return;
            }
            this.savingSummary = true;
            try {
                const response = await this.$axios.post(
                    `api/meetings/${this.meeting_id}/recordings/${this.selectedRecordingId}/update-summary`,
                    { summary: this.editedSummaryContent }
                );
                if (response.data.response || response.data.success) {
                    if (this.transcriptData) {
                        this.transcriptData.summary = this.editedSummaryContent;
                    }
                    this.editingSummary = false;
                    this.editedSummaryContent = '';
                    this.$toast.success('Summary updated successfully');
                } else {
                    throw new Error(response.data.message || 'Failed to update summary');
                }
            } catch (error) {
                console.error('Error saving summary:', error);
                this.$toast.error(error.message || 'Failed to save summary');
            } finally {
                this.savingSummary = false;
            }
        },

        async regenerateSummary() {
            if (!this.meeting_id || !this.selectedRecordingId) {
                this.$toast.error('Cannot regenerate summary - missing meeting or recording information');
                return;
            }

            this.regeneratingSummary = true;
            try {
                this.$toast.info('Regenerating AI summary...');

                const response = await this.$axios.post(
                    `api/meetings/${this.meeting_id}/recordings/${this.selectedRecordingId}/regenerate-summary`
                );

                if (response.data.response || response.data.success) {
                    // Runs in the background now - poll until it's done instead
                    // of expecting the summary in this response.
                    this.startSummaryStatusPolling();
                } else {
                    throw new Error(response.data.message || 'Failed to regenerate summary');
                }
            } catch (error) {
                console.error('Error regenerating summary:', error);
                this.$toast.error(error.message || 'Failed to regenerate summary');
                this.regeneratingSummary = false;
            }
        },

        startSummaryStatusPolling() {
            if (this.summaryStatusPollingInterval) return;

            console.log('[Summary Polling] Starting polling every 4 seconds...');
            this.summaryStatusPollingInterval = setInterval(async () => {
                if (!this.meeting_id || !this.selectedRecordingId) {
                    this.stopSummaryStatusPolling();
                    return;
                }

                try {
                    const response = await this.$axios.get(
                        `api/meetings/${this.meeting_id}/recordings/${this.selectedRecordingId}/summary-status`
                    );

                    if (!response.data.response) return;
                    const { status, summary, error } = response.data.data;

                    if (status === 'processing') return;

                    this.stopSummaryStatusPolling();
                    this.regeneratingSummary = false;

                    if (status === 'failed') {
                        this.$toast.error(error || 'Failed to regenerate summary');
                        return;
                    }

                    if (this.transcriptData && summary) {
                        this.transcriptData.summary = summary;
                    }
                    this.$toast.success('AI summary regenerated successfully');
                } catch (error) {
                    console.error('[Summary Polling] Error:', error);
                }
            }, 4000);
        },

        stopSummaryStatusPolling() {
            if (this.summaryStatusPollingInterval) {
                console.log('[Summary Polling] Stopping polling.');
                clearInterval(this.summaryStatusPollingInterval);
                this.summaryStatusPollingInterval = null;
            }
        },

        async sendSummaryEmail() {
            if (!this.meeting_id) {
                this.$toast.error('Cannot send summary email - missing meeting information');
                return;
            }

            this.sendingSummaryEmail = true;
            try {
                this.$toast.info('Sending meeting summary email...');

                const response = await this.$axios.post(
                    `api/meetings/${this.meeting_id}/send-summary-email`
                );

                if (response.data.response || response.data.success) {
                    this.$toast.success(response.data.message || 'Meeting summary email sent successfully');
                } else {
                    throw new Error(response.data.message || 'Failed to send summary email');
                }
            } catch (error) {
                console.error('Error sending summary email:', error);
                this.$toast.error(error.message || 'Failed to send summary email');
            } finally {
                this.sendingSummaryEmail = false;
            }
        },

        handleVideoTimeUpdate() {
            if (this.$refs.videoPlayer) {
                this.currentVideoTime = this.$refs.videoPlayer.currentTime;
                
                // Throttle scroll updates to improve performance
                if (!this._scrollTimeout) {
                    this._scrollTimeout = setTimeout(() => {
                        this.scrollToActiveSegment();
                        this._scrollTimeout = null;
                    }, 100);
                }
            }
        },

        isSegmentActive(segment) {
            if (!this.currentVideoTime || !segment.start_timestamp) return false;
            
            const endTime = segment.end_timestamp || segment.start_timestamp + 5;
            return this.currentVideoTime >= segment.start_timestamp && this.currentVideoTime < endTime;
        },

        scrollToActiveSegment() {
            if (!this.transcriptData || !this.transcriptData.words) return;
            
            const segments = this.formatTranscriptSegments(this.transcriptData.words);
            const activeIndex = segments.findIndex(segment => this.isSegmentActive(segment));
            
            if (activeIndex !== -1) {
                const segmentRefKey = 'segment-' + activeIndex;
                const segmentRef = this.$refs[segmentRefKey];
                
                if (segmentRef && segmentRef.length > 0 && this.$refs.transcriptContainer) {
                    const element = segmentRef[0];
                    const container = this.$refs.transcriptContainer;
                    
                    // Calculate if element is in view
                    const containerRect = container.getBoundingClientRect();
                    const elementRect = element.getBoundingClientRect();
                    
                    const isInView = (
                        elementRect.top >= containerRect.top &&
                        elementRect.bottom <= containerRect.bottom
                    );
                    
                    // Only scroll if not in view
                    if (!isInView) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }
        },

        seekToSegment(timestamp) {
            if (this.$refs.videoPlayer && timestamp !== undefined) {
                this.$refs.videoPlayer.currentTime = timestamp;
                this.$refs.videoPlayer.play();
            }
        },

        formatTranscriptSegments(words) {
            if (!words || !Array.isArray(words)) return [];
            
            // Group words by speaker and create segments
            const segments = [];
            let currentSegment = null;
            
            words.forEach(word => {
                const speaker = word.speaker || 'Unknown';
                
                if (!currentSegment || currentSegment.speaker !== speaker) {
                    // Start new segment
                    if (currentSegment) {
                        segments.push(currentSegment);
                    }
                    currentSegment = {
                        speaker: speaker,
                        text: word.text || '',
                        start_timestamp: word.start_timestamp,
                        end_timestamp: word.end_timestamp
                    };
                } else {
                    // Add to current segment
                    currentSegment.text += ' ' + (word.text || '');
                    // Update end timestamp to the latest word's end
                    currentSegment.end_timestamp = word.end_timestamp;
                }
            });
            
            // Push last segment
            if (currentSegment) {
                segments.push(currentSegment);
            }
            
            return segments;
        },

        formatTimestamp(seconds) {
            if (!seconds) return '00:00';
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        },

        formatRecordingDate(dateString) {
            if (!dateString) return 'N/A';
            return this.$date(dateString).format(`MMM DD, YYYY ${this.$tz.time}`);
        },

        renderMarkdown(markdownText) {
            if (!markdownText) return '';
            
            // Extract checklist items first
            this.extractChecklistItems(markdownText);
            
            // Simple markdown to HTML converter
            let html = markdownText;
            
            // Headers
            html = html.replace(/^### (.*$)/gim, '<h3 class="text-h6 font-weight-bold mt-4 mb-2">$1</h3>');
            html = html.replace(/^## (.*$)/gim, '<h2 class="text-h5 font-weight-bold mt-4 mb-2">$1</h2>');
            html = html.replace(/^# (.*$)/gim, '<h1 class="text-h4 font-weight-bold mt-4 mb-3">$1</h1>');
            
            // Bold
            html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
            
            // Italic
            html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');
            
            // Checkboxes (unchecked and checked)
            html = html.replace(/^\[ \] (.+)$/gim, '<li style="list-style: none;"><input type="checkbox" disabled style="margin-right: 8px;"> $1</li>');
            html = html.replace(/^\[x\] (.+)$/gim, '<li style="list-style: none;"><input type="checkbox" checked disabled style="margin-right: 8px;"> $1</li>');
            
            // Unordered lists
            html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
            html = html.replace(/(<li>.*<\/li>)/s, '<ul class="ml-4 mb-2">$1</ul>');
            
            // Line breaks
            html = html.replace(/\n\n/g, '</p><p class="mb-2">');
            html = html.replace(/\n/g, '<br>');
            
            // Wrap in paragraph
            html = '<p class="mb-2">' + html + '</p>';
            
            // Remove all <br> tags to eliminate extra spacing
            html = html.replace(/<br\s*\/?>/gi, '');
            
            return html;
        },

        extractChecklistItems(markdown) {
            if (!markdown) {
                this.extractedChecklistItems = [];
                return;
            }

            // Split by newlines and check each line
            const lines = markdown.split('\n');
            const items = [];

            lines.forEach(line => {
                // Match checkbox pattern at start of line
                const uncheckedMatch = line.match(/^\[ \]\s*(.+)$/);
                const checkedMatch = line.match(/^\[x\]\s*(.+)$/i);

                if (uncheckedMatch) {
                    items.push({
                        checked: false,
                        text: uncheckedMatch[1].trim(),
                        selectedAssignee: null,
                        creating: false
                    });
                } else if (checkedMatch) {
                    items.push({
                        checked: true,
                        text: checkedMatch[1].trim(),
                        selectedAssignee: null,
                        creating: false
                    });
                }
            });

            console.log('Extracted checklist items:', items);
            this.extractedChecklistItems = items;
        },

        getAssigneeName(userId) {
            const user = this.availableUsers.find(u => u.id === userId);
            return user ? user.name : 'Unknown User';
        },

        async createBulkTasks() {
            if (!this.allItemsHaveAssignees) {
                this.$toast.error('Please assign someone to each task');
                return;
            }

            this.creatingBulkTasks = true;

            try {
                const taskPromises = this.checkedChecklistItems.map(item => {
                    const taskPayload = {
                        tasks: [{
                            name: item.text,
                            company_id: this.meeting.company_id,
                            meeting_id: this.meeting_id,
                            assignee_ids: [item.selectedAssignee],
                            status_id: 1,
                            priority_id: 3
                        }]
                    };

                    console.log('Creating task:', taskPayload);
                    return this.$axios.post('api/tasks/create_task_v2', taskPayload);
                });

                const results = await Promise.all(taskPromises);
                console.log('All tasks created:', results);

                this.$toast.success(`Successfully created ${this.checkedChecklistItems.length} task${this.checkedChecklistItems.length > 1 ? 's' : ''}`);
                
                // Don't mark as completed yet - let them stay checked so button remains visible
                // The checkboxes will be disabled on next page load when tasks are matched

                // Refresh tasks to update the task list
                if (this.refreshMeetingTasks) {
                    await this.refreshMeetingTasks();
                }

                // Also refresh using getTasks if available
                await this.getTasks();

                // Close dialog but keep items checked (don't switch tabs)
                this.showBulkTaskDialog = false;

            } catch (error) {
                console.error('Error creating bulk tasks:', error);
                this.$toast.error('Failed to create some tasks. Please try again.');
            } finally {
                this.creatingBulkTasks = false;
            }
        },

        async createTaskFromChecklistItem(item) {
            if (!item.selectedAssignee) {
                this.$toast.warning('Please select an assignee');
                return;
            }

            item.creating = true;

            try {
                const taskPayload = {
                    name: item.text,
                    company_id: this.meeting.company_id,
                    meeting_id: this.meeting_id,
                    assignee_ids: [item.selectedAssignee],
                    status_id: 1,
                    priority_id: 3,
                    description: 'Created from AI meeting summary action item'
                };

                const response = await this.$axios.post('api/tasks/create_task_v2', taskPayload);

                if (response.data.response) {
                    this.$toast.success('Task created successfully');
                    
                    // Mark as checked
                    item.checked = true;
                    
                    // Refresh meeting tasks
                    await this.getTasks();
                    
                    // Switch to tasks tab
                    this.activeTab = 0;
                } else {
                    this.$toast.error(response.data.message || 'Failed to create task');
                }
            } catch (error) {
                console.error('Error creating task from checklist item:', error);
                this.$toast.error('Failed to create task');
            } finally {
                item.creating = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.meeting-header-card {
    border-radius: 12px;

    .meeting-meta {
        gap: 8px;

        .meeting-date,
        .meeting-duration {
            display: flex;
            align-items: center;
        }
    }

    .attendees-section {
        .attendee-chip {
            font-weight: 500;
            border-radius: 20px;

            .v-avatar {
                font-size: 0.75rem;
                font-weight: 600;
            }
        }

        .no-attendees {
            padding: 16px;
            background-color: #f5f5f5;
            border-radius: 8px;
            text-align: center;
        }

        // Enhanced user select styling
        .assignee-select {
            &.chip-select {
                .v-select__selections {
                    .v-chip {
                        margin: 2px;
                        border-radius: 16px;
                        height: auto;
                        padding: 2px 8px;

                        .v-avatar {
                            margin-left: 0 !important;
                            margin-right: 6px;
                        }

                        .assignee-initials {
                            font-size: 10px;
                            font-weight: 600;
                            line-height: 1;
                        }
                    }
                }
            }
        }

        // Dropdown item styling
        .user-role-info {
            .role-position {
                font-size: 12px;
                color: #666;
                margin-right: 8px;
            }

            .user-email {
                font-size: 11px;
                color: #999;
            }
        }
    }

    .agenda-content {
        padding: 16px;
        background-color: #f9f9f9;
        border-radius: 8px;
        border-left: 4px solid #1976d2;

        ::v-deep {

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                margin-top: 0;
                margin-bottom: 8px;
            }

            p {
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            ul,
            ol {
                margin-bottom: 8px;
                padding-left: 20px;
            }
        }
    }

    .summary-content {
        ::v-deep {
            h1, h2, h3, h4, h5, h6 {
                color: #1976d2;
                margin-top: 1rem;
                margin-bottom: 0.5rem;
                
                &:first-child {
                    margin-top: 0;
                }
            }

            ul {
                margin-left: 1.5rem;
                margin-bottom: 1rem;
                
                li {
                    margin-bottom: 0.5rem;
                    line-height: 1.6;
                }
            }

            p {
                line-height: 1.7;
                margin-bottom: 1rem;
            }

            strong {
                font-weight: 600;
                color: #333;
            }

            em {
                font-style: italic;
            }
        }
    }

    .meeting-attachments {
        .attachment-card {
            transition: all 0.2s ease;
            border-radius: 8px;

            &:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                transform: translateY(-1px);
            }

            .v-btn {
                text-transform: none;
                font-weight: 500;
            }
        }
    }
}

.meeting-tasks-card {
    border-radius: 12px;

    .meeting-tasks-header {
        border-bottom: 1px solid #e0e0e0;

        .task-filters {
            gap: 12px;

            .user-filter {
                .v-select__selections {
                    .v-chip {
                        font-weight: 500;
                    }
                }
            }
        }
    }

    .empty-tasks {
        .v-icon {
            opacity: 0.6;
        }
    }
}

// Responsive adjustments
@media (max-width: 768px) {
    .meeting-header-card {
        .d-flex.justify-space-between {
            flex-direction: column;
            align-items: flex-start;

            .v-btn {
                margin-top: 16px;
                align-self: stretch;
            }
        }

        .meeting-meta {
            flex-direction: column;
            align-items: flex-start;
        }
    }

    .meeting-tasks-header {
        .task-filters {
            flex-direction: column;
            align-items: stretch;
            width: 100%;

            .user-filter {
                max-width: none !important;
                margin-bottom: 12px;
            }
        }
    }
}

// Markdown content styling - reduce spacing
.markdown-content {
    h1, h2, h3, h4, h5, h6 {
        margin-top: 0.25em;
        margin-bottom: 0.15em;
        line-height: 1.2;
    }
    
    h1 { font-size: 1.5em; }
    h2 { font-size: 1.3em; }
    h3 { font-size: 1.1em; }
    
    p {
        margin-top: 0.15em;
        margin-bottom: 0.15em;
        line-height: 1.4;
    }
    
    ul, ol {
        margin-top: 0.15em;
        margin-bottom: 0.25em;
        padding-left: 1.5em;
        
        li {
            margin-bottom: 0.1em;
            line-height: 1.3;
        }
    }
    
    blockquote {
        margin: 0.25em 0;
        padding-left: 1em;
        border-left: 3px solid #e0e0e0;
    }
    
    strong {
        font-weight: 600;
    }
    
    code {
        background-color: #f5f5f5;
        padding: 0.1em 0.3em;
        border-radius: 3px;
        font-size: 0.9em;
    }
    
    pre {
        background-color: #f5f5f5;
        padding: 0.5em;
        border-radius: 4px;
        overflow-x: auto;
        margin: 0.25em 0;
    }
    
    // Remove excessive spacing for first and last elements
    > *:first-child {
        margin-top: 0;
    }
    
    > *:last-child {
        margin-bottom: 0;
    }
}

.ai-summary-processing-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
}

.ai-robot-illustration {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 180px;
    height: 140px;

    .robot-avatar {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;

        .robot-laptop {
            margin-top: -8px;
        }
    }

    .robot-bubble {
        position: absolute;
        background: #e3f2fd;
        border-radius: 10px;
        padding: 6px 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);

        &.robot-bubble-left {
            left: 0;
            top: 20px;
        }

        &.robot-bubble-top {
            top: 0;
            right: 28px;
        }

        &.robot-bubble-right {
            right: 0;
            top: 20px;
        }
    }
}
</style>